import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import StaffHeader from '../components/StaffHeader';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { SUPPORT_EMAIL } from '../config/support';

// When this page was last checked against the app, in one place. It is rendered
// twice, in the Full Manual title block and in the page footer. It replaces a
// hand-set version number that no build or deploy ever followed (help audit,
// 2026-10-20): change it whenever the help text is revised.
const LAST_UPDATED = 'October 22, 2026';

function Help() {
  const navigate = useNavigate();
  const { isDriver } = useAuth();
  const [activeTab, setActiveTab] = useState('getting-started');
  const [expandedSections, setExpandedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showFullManual, setShowFullManual] = useState(false);

  // Refresh function for pull-to-refresh (resets view state)
  const refreshHelp = useCallback(async () => {
    setSearchQuery('');
    setExpandedSections({});
    // Small delay to show refresh animation
    await new Promise(resolve => setTimeout(resolve, 300));
  }, []);

  const { PullToRefreshIndicator } = usePullToRefresh(refreshHelp);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleViewFullManual = () => {
    setShowFullManual(true);
    // Scroll to top when switching to full manual view
    window.scrollTo(0, 0);
  };

  const handleBackToHelp = () => {
    setShowFullManual(false);
    window.scrollTo(0, 0);
  };

  const handlePrintManual = () => {
    window.print();
  };

  // Accordion Section Component
  const AccordionSection = ({ id, title, children }) => {
    const isExpanded = expandedSections[id];
    const contentString = typeof children === 'string' ? children : '';
    const matchesSearch = !searchQuery ||
      title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contentString.toLowerCase().includes(searchQuery.toLowerCase());

    if (!matchesSearch && searchQuery) return null;

    return (
      <div className="accordion-section">
        <button
          className={`accordion-header ${isExpanded ? 'expanded' : ''}`}
          onClick={() => toggleSection(id)}
        >
          <span>{title}</span>
          <span className="accordion-icon">{isExpanded ? '−' : '+'}</span>
        </button>
        {isExpanded && (
          <div className="accordion-content">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="help-page">
      {/* Header */}
      <StaffHeader active="help" className="no-print" />

      <PullToRefreshIndicator />

      <main className="help-main">
        {showFullManual ? (
          /* Full Manual View - All content expanded for printing */
          <div className="full-manual">
            <div className="full-manual-header no-print">
              <button className="btn btn-secondary" onClick={handleBackToHelp}>
                ← Back to Documentation
              </button>
              <button className="btn btn-primary" onClick={handlePrintManual}>
                Print / Save as PDF
              </button>
            </div>

            <div className="manual-title-section">
              <h1>SnowTrack™ User Manual</h1>
              <p className="manual-version">Last updated {LAST_UPDATED} • Complete Documentation</p>
            </div>

            <div className="manual-toc">
              <h2>Table of Contents</h2>
              <ol>
                <li><a href="#manual-getting-started">Getting Started</a></li>
                <li><a href="#manual-customers">Customer Management</a></li>
                <li><a href="#manual-routes">Routes</a></li>
                <li><a href="#manual-storm-mode">Storm Mode</a></li>
                <li><a href="#manual-billing">Billing & Payments</a></li>
                <li><a href="#manual-history">Service History</a></li>
                <li><a href="#manual-expenses">Expenses</a></li>
                <li><a href="#manual-reports">Reports</a></li>
                <li><a href="#manual-settings">Settings</a></li>
                <li><a href="#manual-referrals">Referral Discounts</a></li>
                <li><a href="#manual-faq">FAQ</a></li>
              </ol>
            </div>

            <section id="manual-getting-started" className="manual-section">
              <h2>1. Getting Started</h2>

              <h3>Creating Your Account</h3>
              <ol>
                <li>Visit <strong>snowtrack.pro</strong></li>
                <li>Click <strong>Start Free Trial</strong></li>
                <li>Enter your company information (name, email, phone, password)</li>
                <li>Optional: tick <strong>Email me SnowTrack product news, tips and offers</strong>. It starts unticked, and you can change it later in Settings, Account</li>
                <li>Your account starts a <strong>free trial</strong> of one storm: 72 hours from the first stop you complete, with full access to try all features</li>
              </ol>

              <h3>Dashboard Overview</h3>
              <p>Your Dashboard displays key business metrics:</p>
              <ul>
                <li><strong>Total Routes</strong> - All routes created</li>
                <li><strong>Active Routes</strong> - Routes currently in progress</li>
                <li><strong>Customers</strong> - Number of customers in your account (not shown to drivers)</li>
                <li><strong>Total Stops</strong> - Stops across your routes</li>
                <li><strong>Weather Widget</strong> - Current conditions and forecasts (GPS or home location)</li>
                <li><strong>Financial Summary</strong> - Revenue, expenses and net profit (owner and admin)</li>
                <li><strong>New signups from your intake form</strong> - Customers who signed up through your
                form and have not been reviewed yet, with Review and Mark reviewed buttons (owner and admin)</li>
              </ul>

              <h3>Navigation</h3>
              <ul>
                <li><strong>Dashboard</strong> - Business overview and weather</li>
                <li><strong>Customers</strong> - Manage customer accounts</li>
                <li><strong>Routes</strong> - Create and manage service routes</li>
                <li><strong>History</strong> - View completed services (owner and admin)</li>
                <li><strong>My History</strong> - Your own routes and pay (crew leads and drivers)</li>
                <li><strong>Expenses</strong> - Track business expenses (Pro tier)</li>
                <li><strong>Reports</strong> - Profit & Loss analysis (Pro tier)</li>
                <li><strong>Storm Mode</strong> - Quick storm service management</li>
                <li><strong>Settings</strong> - Company and account configuration</li>
              </ul>

              <h3>Add SnowTrack™ to your home screen</h3>
              <p>iPhone:</p>
              <ol>
                <li>Open snowtrack.pro in Safari</li>
                <li>Tap the Share button (the square with an arrow pointing up)</li>
                <li>Scroll down and tap Add to Home Screen, then tap Add</li>
              </ol>
              <p>Android:</p>
              <ol>
                <li>Open snowtrack.pro in Chrome</li>
                <li>Tap the menu (the three dots, top right)</li>
                <li>Tap Add to Home screen or Install app, then confirm</li>
              </ol>
              <p>Your customers can add their portal the same way; the portal shows them how, and its
              icon is named My Snow Service.</p>

              <h3>Updates</h3>
              <p>When a new version of SnowTrack™ is ready, a bar at the bottom of the screen reads
              &quot;A new version of SnowTrack is ready. Tap to update.&quot; The app checks every time you
              open it and every time you come back to it. Nothing reloads until you tap the bar, so a
              form you are halfway through is not lost.</p>
            </section>

            <section id="manual-customers" className="manual-section">
              <h2>2. Customer Management</h2>

              <h3>Adding Customers</h3>
              <ol>
                <li>Navigate to <strong>Customers</strong></li>
                <li>Click <strong>+ Add Customer</strong></li>
                <li>Fill in customer details and click <strong>Save</strong></li>
              </ol>

              <h3>Customer Fields</h3>
              <ul>
                <li><strong>First Name / Last Name</strong> - Required for Square invoicing</li>
                <li><strong>Email</strong> - Required for sending invoices</li>
                <li><strong>Phone</strong> - Tap to call or text</li>
                <li><strong>Service Address</strong> - Where you provide service</li>
                <li><strong>Pricing Tier</strong> - Select from your configured tiers</li>
                <li><strong>Custom Pricing</strong> - Override tier pricing</li>
                <li><strong>Salt Preference</strong> - No Salt, Always Salt, or Salt As Needed (chosen at each visit in Storm Mode)</li>
                <li><strong>Lead Source</strong> - How they found you</li>
                <li><strong>Referred By</strong> - Select referring customer for referral discounts</li>
              </ul>

              <h3>Multiple Properties</h3>
              <p>Customers can have multiple service addresses. Each property can have its own pricing tier and salt preference.</p>

              <h3>Discounts</h3>
              <ul>
                <li><strong>Percentage Discount</strong> - e.g., 10% off</li>
                <li><strong>Dollar Discount</strong> - e.g., $5 off per service</li>
              </ul>

              <h3>Setting Up Texting (Pro)</h3>
              <p>Texting is set up in <strong>Settings &gt; Texting and Email &gt; How texts are sent</strong>.
              Owners and admins see that card on the Pro plan and up when texting is available. Choose a mode:</p>
              <ul>
                <li><strong>Off</strong> - No texts are sent.</li>
                <li><strong>SnowTrack number</strong> - SnowTrack provides a local number and handles carrier
                registration. Only the owner can click <strong>Set up my texting number</strong>; the card shows
                the cost before anything is charged. Then the owner enters the business details the carriers
                need, and the card shows each step until the carriers approve the number.</li>
                <li><strong>My own Twilio</strong> - Texts go out through your own Twilio account: enter its
                Account SID, Auth Token and Messaging Service SID. Afterwards the card shows only the last
                four characters of each.</li>
              </ul>
              <p>The owner can click <strong>Send a test text to my phone</strong> to send one text to the
              mobile number on their profile, up to 5 an hour.</p>

              <h3>Texting Costs and Your Number</h3>
              <ul>
                <li><strong>Setup fee</strong> - A one-time fee, $25 by default, charged to the card on your
                subscription when the owner sets up a SnowTrack number.</li>
                <li><strong>Texting-number line</strong> - Once carriers approve your number, a line of $5 a
                month by default is added to your subscription.</li>
                <li><strong>When your plan ends</strong> - Your number is kept for 30 days and texts stop. The
                card shows the date it will be released, and the owner gets one email about it. Resubscribe
                before then to keep the number. After that date the number is released and the monthly line
                comes off your subscription.</li>
                <li><strong>Coming back</strong> - After a release, <strong>Set up a new texting number</strong>
                gets a new number for a one-time reinstatement fee, $25 by default, instead of the setup fee.
                If the card says there is no reinstatement fee, nothing is charged.</li>
              </ul>
              <p>These are the standard amounts; the card always shows the amount it will charge.</p>

              <h3>Your Policy Pages</h3>
              <p>Carriers need a texting privacy policy and terms page for your business. Leave both blank
              and SnowTrack™ hosts them for you, at snowtrack.pro/sms-policy/your-company/privacy and
              /terms. If your website has its own, the owner enters both pages under <strong>Your policy
              pages</strong> on the same card: both or neither, each starting https://. They can be changed
              until your texting campaign goes to the carriers for review, or after the carriers reject it.</p>

              <h3>Texts (Pro)</h3>
              <p>Once texting is set up, owners and admins can text customers who agreed to texts.</p>
              <ul>
                <li><strong>Bulk texts</strong> - Message Customers, then Send as Text (or Both). The counter shows characters and segments; &quot;Reply STOP to opt out.&quot; is added for you.</li>
                <li><strong>Quiet hours</strong> - bulk texts go out from 8:00 AM to 9:00 PM in your company's time zone only. Set the zone in Settings, Company.</li>
                <li><strong>STOP and HELP</strong> - a customer who replies STOP is never texted again unless they reply START.</li>
                <li><strong>Replies</strong> - texts from customers are emailed to your business email and kept on the customer card under Texts.</li>
                <li><strong>Text one customer</strong> - under Texts on the customer card, type in the box and click Send. Consent, STOP, Do Not Serve and the daily limit apply; quiet hours do not. Their reply lands in the same thread.</li>
                <li><strong>Message History</strong> - beside Message Customers: every bulk email and text, with delivery counts. Open a bulk text to see each customer it went to, their name in bold.</li>
              </ul>
            </section>

            <section id="manual-routes" className="manual-section">
              <h2>3. Routes</h2>

              <h3>Creating a Route</h3>
              <ol>
                <li>Navigate to <strong>Routes</strong></li>
                <li>Click <strong>+ New Route</strong> (owner and admin)</li>
                <li>Enter the route name, a Storm ID, who it is assigned to, and optional starting/ending addresses</li>
                <li>Configure invoice settings if using Square invoicing</li>
                <li>Add customers as stops</li>
              </ol>

              <h3>Managing Stops</h3>
              <ul>
                <li><strong>Add stops</strong> - Click <strong>Stops</strong> on the route card, then <strong>+ Add</strong> beside each customer</li>
                <li><strong>Order</strong> - Click <strong>Optimize Route</strong> to put the stops in the best driving order</li>
                <li><strong>Remove</strong> - Remove a stop that has not been completed</li>
              </ul>

              <h3>Route Statuses</h3>
              <ul>
                <li><strong>Not Started</strong> - Ready to begin</li>
                <li><strong>In Progress</strong> - Currently being serviced</li>
                <li><strong>Completed</strong> - All stops finished</li>
                <li><strong>Archived</strong> - Historical record</li>
              </ul>

              <h3>Invoice Settings per Route</h3>
              <p>Customize invoices using variables:</p>
              <ul>
                <li><code>{'{route_name}'}</code> - Route name</li>
                <li><code>{'{service_date}'}</code> - Date of service</li>
                <li><code>{'{customer_name}'}</code> - Customer name</li>
              </ul>
            </section>

            <section id="manual-storm-mode" className="manual-section">
              <h2>4. Storm Mode</h2>

              <h3>Starting Storm Mode</h3>
              <ol>
                <li>Click <strong>Storm Mode</strong> in navigation</li>
                <li>Tap a route</li>
                <li>For a route that has not started, choose your starting point: saved location or current GPS location</li>
              </ol>

              <h3>Telling the Customer You Are On the Way</h3>
              <p>When the <strong>Storm Mode arrival alert</strong> is on (Settings, Texting and Email, Pro),
              the customer is told you are coming: a text if they agreed to texts, otherwise an email if they
              have an address and are subscribed. It goes out when the driver taps <strong>Navigate</strong> or
              {' '}<strong>On my way</strong> on a stop, and when the driver completes or skips a stop. Completing
              or skipping only sends it on a route from today or yesterday. Each stop gets it once.</p>
              <ul>
                <li><strong>Text customers when the driver is</strong> - <strong>Next stop only</strong> (the
                default), <strong>Next 2 stops</strong> or <strong>Next 3 stops</strong>. With Next 3 stops,
                completing a stop tells each of the next three open stops that has not been told yet.</li>
                <li><strong>Message</strong> - Your own wording, with the fields {'{{customer_name}}'},
                {' '}{'{{property_address}}'}, {'{{company_name}}'} and {'{{stops_away}}'}.
                {' '}{'{{stops_away}}'} becomes &quot;1 stop&quot;, &quot;2 stops&quot; or &quot;3 stops&quot;: how far
                the stop is from the driver. The default text is &quot;{'{{company_name}}'}: we&apos;re about
                {' '}{'{{stops_away}}'} away from {'{{property_address}}'}. Reply STOP to opt out.&quot; A message
                can be up to 3 text segments; <strong>Use default text</strong> puts the standard wording back.</li>
              </ul>
              <p>The stop then shows what happened:</p>
              <ul>
                <li><strong>Texted 7:42 AM</strong> or <strong>Emailed 7:42 AM</strong> - the customer was told (your company's time zone)</li>
                <li><strong>Not notified</strong> - with the reason: no text consent, no contact, texting off, or do not serve</li>
                <li><strong>Notify failed</strong> - the text or email could not be sent; it is not retried</li>
              </ul>
              <p>The note stays on the stop after it is completed. Completed and skipped stops have no On my way button.</p>

              <h3>Completing a Stop</h3>
              <ol>
                <li>Tap <strong>Done</strong> on the Up Next stop</li>
                <li>Optionally take a photo</li>
                <li>Tick <strong>Apply Salt</strong> (Salt As Needed customers only) and any add-on services</li>
                <li>Choose how the customer is paying</li>
              </ol>

              <h3>Payment Options</h3>
              <ul>
                <li><strong>Venmo, Cash, Cash App, Zelle, Check - Paid</strong>, or any method you added - Money received</li>
                <li><strong>Venmo, Cash, Cash App, Zelle, Check - Not Paid</strong>, any method you added, or <strong>Not Paid</strong> - Collect later</li>
                <li><strong>Square</strong> - Bill by Square invoice (no card is charged at the stop). With
                {' '}<strong>Send the Square invoice automatically when a stop is completed</strong> on (Settings,
                Invoice Defaults), the invoice is emailed when the stop is completed; otherwise send it later
                with Bill All</li>
                <li><strong>Mark as Comp/Free</strong> - No charge, with a reason</li>
              </ul>
              <p>A payment method you add yourself in Settings gets its own Paid and Not Paid buttons here,
              like the built-in ones, once it is switched on.</p>

              <h3>Entering the Storm Depth</h3>
              <p>On a route priced by snow depth, a stop completed before the depth is known shows
              <strong>Awaiting depth</strong> instead of a price, and Storm Mode shows a banner:
              "N completed stops are waiting for a measured depth." Owners and admins enter the depth
              once the storm total is in:</p>
              <ol>
                <li>Tap <strong>Enter depth</strong> on the Storm Mode banner, or the amber
                <strong>Awaiting depth</strong> chip on the route card in Routes. When the last stop on the
                route is completed or skipped, the box opens on its own once; you can close it and come back.</li>
                <li>Enter the <strong>storm depth</strong> in inches (one decimal, e.g. 6.5) and the source.</li>
                <li>Optionally give a <strong>different depth for a city</strong> in the per-city boxes. Blank
                uses the route depth.</li>
                <li>Check the <strong>preview</strong>: every completed stop with its band, old and new price,
                and Paid, Invoiced or Comp badges. Nothing is saved until you confirm.</li>
                <li>Click <strong>Save depth</strong>. Every completed stop on the route is repriced and locked
                at its band. Stops completed afterwards are priced straight away.</li>
              </ol>
              <ul>
                <li><strong>Other routes in the same storm:</strong> tick <strong>Also apply</strong> to give
                the same depth to other depth routes with the same Storm ID that have none yet.</li>
                <li><strong>Changing it later:</strong> the grey chip (e.g. "6.5 in · 2 city overrides")
                reopens the box. <strong>History</strong> lists every change.</li>
                <li><strong>Missing prices:</strong> if a size has no price for a band, nothing is saved; fill
                the grid in Settings first.</li>
                <li><strong>Paid at the door:</strong> on a stop awaiting depth, enter the <strong>Amount
                collected</strong> for every method except Square. <strong>Square, invoice after
                depth</strong> records the stop as Square and not paid, with no amount; it is invoiced once the
                depth is entered (automatically when Auto-send is on, or with Bill All). After the depth, the
                <strong>true-up</strong> list shows paid stops whose price differs from what was collected; the
                balance shows on the statement.</li>
                <li><strong>Totals:</strong> stops awaiting depth are never counted as $0.00. The Route Complete
                bar and the Paid, Unpaid and Total boxes show the priced amount and "+ N awaiting depth", or
                <strong>Awaiting depth</strong> when nothing on the route is priced yet.</li>
                <li><strong>Up Next prices:</strong> before the depth is entered, a stop shows its range by depth,
                e.g. "By depth: $50.00-$95.00" (first band to top band for the customer's size, on-call premium
                included). After it is entered: the band price, e.g. "$65.00 (6.1-9 in)". Completed stops show
                their band beside the price.</li>
                <li><strong>Already invoiced:</strong> changing the depth on an invoiced stop needs a second
                tick to confirm. The sent invoice is not changed. Use <strong>Bill the difference</strong>
                on the stop (Pro) to send one more invoice for the increase over what was invoiced; if the
                price went down, nothing is sent.</li>
              </ul>

              <h3>Storm ID Importance</h3>
              <p>A Storm ID is a label you give each route, such as the date. Give every route in the same
              storm the same Storm ID: referral discounts are worked out per Storm ID.</p>
            </section>

            <section id="manual-billing" className="manual-section">
              <h2>5. Billing & Payments</h2>

              <h3>Bill All Feature</h3>
              <ol>
                <li>Complete all stops on a route (Pro tier, Square connected)</li>
                <li>Click <strong>Bill All</strong> on the completed route in Storm Mode</li>
                <li>Invoices are sent for unpaid stops where you chose Square, for customers with email addresses</li>
              </ol>

              <h3>Invoice Line Items</h3>
              <ul>
                <li><strong>Snow Removal</strong> - Base service price, plus any extra charge</li>
                <li><strong>Salt Application</strong> - Salt price (if applied)</li>
                <li>One line for each add-on service</li>
                <li>Customer discounts are applied; referral discounts are not</li>
              </ul>

              <h3>Customer Requirements for Invoicing</h3>
              <ul>
                <li>First and Last Name (required for Square)</li>
                <li>Email Address (where invoice is sent)</li>
              </ul>
            </section>

            <section id="manual-history" className="manual-section">
              <h2>6. Service History</h2>

              <h3>Viewing History</h3>
              <p>Filter completed services by date range, customer, route, or payment status.</p>

              <h3>Payment Status</h3>
              <ul>
                <li><strong>Not Paid</strong> - Awaiting payment</li>
                <li><strong>Paid</strong> - Payment received, shown with the method</li>
                <li><strong>Invoice Sent</strong> - A Square invoice is out and unpaid</li>
              </ul>
              <p>Depth-priced services show their band under the address ("Snow removal, 6.1-9 in") and
              "On-call premium (20%)" where it applied. A service waiting for the storm depth shows
              <strong>Awaiting depth</strong> instead of a price and is left out of Revenue. Mark Paid on one
              asks for the amount collected, or offers <strong>Square, invoice after depth</strong>.</p>

              <h3>Recording Payments</h3>
              <ol>
                <li>Find the service in history</li>
                <li>Click <strong>Mark Paid</strong></li>
                <li>Select payment method</li>
                <li>Check the <strong>Payment Date</strong>. It starts at today in your company&apos;s time zone; change it if the payment came in on another day</li>
              </ol>
            </section>

            <section id="manual-expenses" className="manual-section">
              <h2>7. Expenses (Pro Tier)</h2>

              <h3>Adding Expenses</h3>
              <ol>
                <li>Navigate to <strong>Expenses</strong></li>
                <li>Click <strong>+ Add Expense</strong></li>
                <li>Enter date, amount, category, description</li>
                <li>Optionally link to a route</li>
              </ol>

              <h3>Categories</h3>
              <p>New accounts start with no categories: add your own in Settings, Invoicing and
              Payments, Expense Categories. Accounts created before categories were introduced also
              have Labor, Materials, Fuel, Equipment, Repairs, Salt and Other.
              {' '}<strong>Processing Fees</strong> is created for you the
              first time a Square payment fee is recorded. Built-in categories can be switched off
              but not deleted; categories you added can be deleted.</p>
            </section>

            <section id="manual-reports" className="manual-section">
              <h2>8. Reports (Pro Tier)</h2>

              <h3>Profit & Loss Analysis</h3>
              <p>Date range options: This Month, Last Month, This Season (your season, set in Settings &gt; Company &gt; Season), Custom</p>

              <h3>Metrics</h3>
              <ul>
                <li><strong>Total Revenue</strong> - Sum of all completed services</li>
                <li><strong>Total Expenses</strong> - Sum of all expenses</li>
                <li><strong>Net Profit</strong> - Revenue minus expenses</li>
                <li><strong>Profit Margin</strong> - Net profit as percentage</li>
              </ul>

              <h3>Breakdowns</h3>
              <ul>
                <li>Revenue by payment status (Paid/Unpaid)</li>
                <li>Revenue by payment method</li>
                <li>Expenses by category</li>
              </ul>
            </section>

            <section id="manual-settings" className="manual-section">
              <h2>9. Settings</h2>

              <p>Settings is one page of seven groups, each opened and closed by its title: Company,
              Pricing, Customers, Texting and Email, Invoicing and Payments, Team, and Account. Owners
              and admins see all seven, and Company opens first. Drivers and crew leads see Account only,
              and it opens first. Most cards have their own Save button. A few save as soon as you change
              them, such as the Public Rates Page switch and Service Requests.</p>

              <h3>Company</h3>
              <ul>
                <li><strong>Company Information</strong> - Company name, phone, email (shown on the customer portal), address (printed on statements) and time zone</li>
                <li><strong>Season</strong> - The months your season runs. It sets "This season" on the Dashboard, Reports and statements</li>
                <li><strong>Weather Location</strong> - Where the Dashboard forecast is for (see below)</li>
                <li><strong>Route Optimization</strong> - Where Storm Mode starts when it puts a route in order (see below)</li>
              </ul>

              <h3>Pricing Tiers</h3>
              <p>Create customizable pricing categories with a name, what the size means (shown on your
              signup form), flat price, and salt price. The flat price is used when depth pricing is
              off, and for routes created before it was switched on.</p>

              <h3>Snow Depth Pricing</h3>
              <p>Price each storm by the measured snow depth. Set the depth bands (by default 0-3, 3.1-6,
              6.1-9, 9.1-12 and 12.1+ inches), then fill the grid of prices: one row per size, one column
              per band. Empty cells are highlighted, and Salt is shown read only because it stays one price
              per size. Set the on-call premium (default 20%), which is added to snow, salt and add-ons
              before discounts for customers set to On-call, only on routes priced by depth. An add-on
              with <strong>On-call premium applies</strong> unticked is left out. Click <strong>Save depth
              pricing</strong> to keep your changes. Depth pricing can be switched on once every
              price is filled and every active customer has a size. It applies to new routes only:
              existing routes and completed stops do not change. It sits under Pricing Tiers in the
              Pricing group.</p>

              <h3>Rest of Pricing</h3>
              <ul>
                <li><strong>Service Add-ons</strong> - Extra services offered when completing a stop. Each has a default price and an optional price per pricing tier, either a flat amount or a percent of the stop&apos;s snow and salt. Each can be shown or hidden on your rates page and signup form (<strong>Show on rates page and signup</strong>), left out of the on-call premium, and can <strong>Ask for a detail</strong> such as &quot;Done by&quot;. Delete removes one, or moves it to Inactive Add-ons if it was already used, where Reactivate brings it back. Click <strong>Save Add-ons</strong> to keep your changes</li>
                <li><strong>Public Rates Page</strong> - The link to your public rates page, and whether it shows your prices</li>
                <li><strong>Referral credit (shown in Storm Mode)</strong> - See Referral Discounts below</li>
              </ul>

              <h3>Customers</h3>
              <ul>
                <li><strong>Signup link</strong> - Your public signup link, ready to copy, with an optional custom ending. The custom ending changes both your signup link and your rates page link; the original links keep working</li>
                <li><strong>Intake Form Disclaimer</strong> - A payment disclaimer your customers must accept before submitting the signup form, in your own words. The same card sets the driveway size confirmation line shown under the size question, which stays on even when the disclaimer is off</li>
                <li><strong>Lead Sources</strong> - How customers find you, with a count for each</li>
                <li><strong>Welcome Message</strong> - Sent once to a new signup when you confirm their driveway size</li>
                <li><strong>Service Requests</strong> - Let on-call customers request service in the portal</li>
              </ul>

              <h3>Texting and Email</h3>
              <ul>
                <li><strong>How texts are sent</strong> and <strong>Storm Mode arrival alert</strong> - Shown on the Pro plan and up when texting is available. This is where texting is set up (see Setting Up Texting above)</li>
                <li><strong>Season Check-in Message</strong> - Always shown, because it is sent by email as well as by text</li>
              </ul>

              <h3>Invoicing and Payments</h3>
              <ul>
                <li><strong>Square Account</strong> - Connect your own Square account, pick a location if you have more than one, and disconnect to revoke access. Invoices are sent from the account you connect</li>
                <li><strong>Invoice Defaults</strong> - The title and message on the Square invoices SnowTrack™ sends, and whether a Square invoice is sent automatically when a stop is completed</li>
                <li><strong>Payment methods and fees</strong> - One row per payment method: switch it on or off, and set its fee as a percentage plus a flat amount. Add your own methods, and they carry a fee too. Only the methods switched on are offered on the customer form; a customer whose method has since been switched off or deleted keeps it, shown as "(off)", so opening their form does not change it. Without a connected Square account, Square is left off the customer form and a new customer, property or intake signup starts on the first method that is on. A method you added is a payment choice in Storm Mode and in Mark Paid, like the built-in ones, and shows by its name on stops, History, Reports and the print summary. The fees appear on the route print summary. Square's fee is also what is recorded in Expenses when a Square payment arrives</li>
                <li><strong>Expense Categories</strong> - Add your own categories with Add Category, switch any category on or off, and delete the ones you added. Categories cannot be renamed, and built-in ones cannot be deleted</li>
              </ul>

              <h3>Team</h3>
              <p>Team Members: invite, edit and remove team members. Admins also see your plan here.</p>

              <h3>Account</h3>
              <ul>
                <li><strong>Profile Information</strong> and <strong>Change Password</strong> - Everyone</li>
                <li><strong>Product Email</strong> - Owner only. Tick <strong>Email me SnowTrack product news, tips and offers</strong> to get SnowTrack's own product email at your login email; untick it to stop. It saves as soon as you click. Every product email also has an unsubscribe link that turns it off in one click. Account email (password resets, billing) is not affected</li>
                <li><strong>Your SnowTrack™ Subscription</strong> - Owner only. Shows your plan, your subscription status, and your usage of users and customers against your limits. Manage Billing opens the Stripe billing portal to change plan or update your card. Cancel Subscription keeps access to the end of the paid period, then the account becomes read-only; you can set an expected return month for one reminder email</li>
                <li><strong>Suggest a Feature</strong> - Everyone</li>
                <li><strong>Delete Account</strong> - Owner only, last on the page</li>
              </ul>

              <h3>Weather Location</h3>
              <ul>
                <li><strong>Use the location of the phone or computer you are on</strong> - Weather for where you are</li>
                <li><strong>Use home location</strong> - Set a fixed city/state</li>
              </ul>
              <p>After you save a home city, if the National Weather Service puts that city in a
              different time zone from your company&apos;s, a line under the card names both zones as the Time zone list does (for example Mountain (Denver)) and offers
              {' '}<strong>Use</strong> that zone; nothing changes unless you click it, and
              {' '}<strong>Dismiss</strong> hides it until your next save.</p>

              <h3>Route Optimization</h3>
              <p>With <strong>Start route optimization from the driver's current location</strong> on,
              the driver's phone location is the starting point. When off, the route's starting address
              is used, or the last completed stop once one is done.</p>
            </section>

            <section id="manual-referrals" className="manual-section">
              <h2>10. Referral Discounts</h2>

              <h3>How Referrals Work</h3>
              <p>When a customer refers someone who also receives service in the same storm, the referring customer gets a discount.</p>

              <h3>Setup</h3>
              <ol>
                <li>Go to <strong>Settings → Pricing → Referral credit (shown in Storm Mode)</strong></li>
                <li>Set the <strong>Credit per referral</strong> (e.g., $5.00)</li>
                <li>Set the <strong>Lowest price after referral credit</strong>. The price shown never goes below it; it does not set a minimum price for any other stop</li>
              </ol>

              <h3>Example</h3>
              <p>If discount is $5/referral and Customer A referred 2 people who got service in the same storm, Customer A's stop shows $10 off.</p>

              <h3>Important</h3>
              <p>Referral discounts only apply when both referrer and referred customer are serviced in the same storm (Storm ID must be set).
              The discount is shown on the stop in Storm Mode for reference; it is not taken off the stop's charged
              price or Square invoices, so use the Discount field when completing the stop if you want to charge it.</p>
            </section>

            <section id="manual-faq" className="manual-section">
              <h2>11. Frequently Asked Questions</h2>

              <h3>How does the free trial work?</h3>
              <p>Your trial includes one complete storm event: 72 hours from the first stop you
              complete. A route started inside those 72 hours can always be finished. No credit card is
              required to start and nothing is charged automatically.</p>

              <h3>How does pricing work?</h3>
              <p>SnowTrack™ uses flat monthly pricing. There are no per-storm charges. Each plan
              includes a set number of users and customers, and unlimited properties under each
              customer. These are the standard prices; current prices are on the Pricing page.</p>
              <ul>
                <li><strong>Basic, $10/month</strong>: 1 user, 25 customers. Route building and
                optimization, Storm Mode, weather forecasts, the customer intake form, service
                history, photo uploads, the customer portal and referral discounts.</li>
                <li><strong>Pro, $25/month</strong>: 3 users, 75 customers. Everything in Basic, plus
                Square invoicing from your own account, payment status tracking, expense tracking,
                profit and loss reports, bulk customer email and customer texting. A SnowTrack texting
                number has its own setup fee and monthly line (see Texting Costs and Your Number).</li>
                <li><strong>Premium, $49/month</strong>: 10 users, 200 customers. Everything in Pro,
                plus priority email support.</li>
                <li><strong>Enterprise, $99/month</strong>: unlimited users and customers. Everything
                in Premium, plus a dedicated account manager, custom integrations and onboarding
                assistance.</li>
              </ul>
              <p>Reaching a limit stops you adding more until you upgrade or free a slot. It never
              removes anything you already have, and routes, Storm Mode, invoicing and reports keep
              working. Deactivating a customer frees a slot.</p>

              <h3>How do I start a paid plan or change plans?</h3>
              <p>Pick Basic, Pro or Premium on the pricing page and pay by card. Enterprise is arranged
              by email instead. Checkout is handled by Stripe, so your card details go to Stripe and never
              to us. To change plan, update your card or cancel, use the billing portal in Settings. Plans
              are month-to-month with no contract. When you change plan, Stripe prorates the
              difference.</p>


              <h3>Can a customer have multiple properties?</h3>
              <p>Yes! Each customer can have multiple service addresses with their own pricing.</p>

              <h3>Can customers sign up themselves?</h3>
              <p>Yes. Your link is shown ready to copy in Settings, Customers, Signup link.
              It looks like https://snowtrack.pro/new-customer/your-company</p>
            </section>

            <div className="manual-footer">
              <p>SnowTrack™ • © {new Date().getFullYear()} • {SUPPORT_EMAIL}</p>
            </div>
          </div>
        ) : (
          /* Regular Help Center View */
          <>
        {/* Page Title */}
        <div className="help-title-section">
          <h1 className="help-title">Documentation</h1>
          <p className="help-subtitle">Complete guide to using SnowTrack™ for your snow removal business</p>
        </div>

        {/* Search and Actions */}
        <div className="help-actions no-print">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button className="clear-search" onClick={() => setSearchQuery('')} aria-label="Clear search">×</button>
            )}
          </div>
          <div className="action-buttons">
            <button className="btn btn-primary" onClick={handleViewFullManual}>
              View Full Manual
            </button>
            <button className="btn btn-secondary" onClick={handlePrint}>
              Print This Page
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="help-tabs no-print">
          <button
            className={`help-tab ${activeTab === 'getting-started' ? 'active' : ''}`}
            onClick={() => setActiveTab('getting-started')}
          >
            Getting Started
          </button>
          <button
            className={`help-tab ${activeTab === 'features' ? 'active' : ''}`}
            onClick={() => setActiveTab('features')}
          >
            Features
          </button>
          <button
            className={`help-tab ${activeTab === 'faq' ? 'active' : ''}`}
            onClick={() => setActiveTab('faq')}
          >
            FAQ
          </button>
          <button
            className={`help-tab ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            Contact
          </button>
        </div>

        {/* Content Area */}
        <div className="help-content">
          {/* Getting Started Tab */}
          {activeTab === 'getting-started' && (
            <div className="tab-content">
              <h2 className="section-title">Getting Started with SnowTrack™</h2>
              <p className="intro-text">
                Welcome to SnowTrack™! This comprehensive guide will help you set up your account and start managing
                your snow removal business efficiently.
              </p>

              <AccordionSection id="gs-account" title="Account Setup & First Login">
                <h4>Creating Your Account</h4>
                <ol>
                  <li>Visit <strong>snowtrack.pro</strong></li>
                  <li>Click <strong>Start Free Trial</strong></li>
                  <li>Enter your company information:
                    <ul>
                      <li>Company name</li>
                      <li>Your name, email, and phone</li>
                      <li>Create a password</li>
                    </ul>
                  </li>
                  <li>Optional: tick <strong>Email me SnowTrack product news, tips and offers</strong>. It starts unticked. Every product email has an unsubscribe link, and you can turn it on or off later in Settings, Account</li>
                  <li>Your account starts a <strong>free trial</strong> of one storm: 72 hours from the first stop you complete, with full access to try all features</li>
                </ol>

                <h4>First Login</h4>
                <p>After registering, you'll be taken to your Dashboard. We recommend completing these steps:</p>
                <ol>
                  <li><strong>Settings</strong> - Set up your pricing tiers and invoice defaults</li>
                  <li><strong>Customers</strong> - Add your customer list</li>
                  <li><strong>Routes</strong> - Organize customers into service routes</li>
                </ol>
              </AccordionSection>

              <AccordionSection id="gs-dashboard" title="Dashboard Overview">
                <h4>Key Metrics</h4>
                <p>Your Dashboard displays at-a-glance business metrics:</p>
                <ul>
                  <li><strong>Total Routes</strong> - All routes created</li>
                  <li><strong>Active Routes</strong> - Routes currently in progress</li>
                  <li><strong>Customers</strong> - Number of customers in your account (not shown to drivers)</li>
                  <li><strong>Total Stops</strong> - Stops across your routes</li>
                </ul>
                <p>Owners and admins also see a <strong>Financial Summary</strong> (revenue, expenses and
                net profit for This Month, Last Month, This Season, Year to Date or All Time), and a
                banner listing properties whose driveway size needs confirming.</p>

                <h4>New Signups From Your Intake Form</h4>
                <p>Owners and admins see a card at the top of the Dashboard when customers have signed up
                through your intake form and not been reviewed yet. Each one shows the name, address,
                service preference, driveway size, salt choice and signup date.</p>
                <ul>
                  <li><strong>Review</strong> - Opens that customer's Edit form on the Customers page. Saving
                  it marks the signup reviewed.</li>
                  <li><strong>Mark reviewed</strong> - Takes it off the card without opening the form.</li>
                  <li>Reactivating or deactivating a customer does not count as reviewing it. The card is
                  hidden when there is nothing to review.</li>
                </ul>

                <h4>Weather Widget</h4>
                <p>The weather widget shows current conditions and forecasts:</p>
                <ul>
                  <li>Current temperature and conditions</li>
                  <li>Snowfall predictions</li>
                  <li>Toggle between GPS location and home location (set in Settings)</li>
                </ul>

                <h4>Quick Actions</h4>
                <p>Common actions available from Dashboard:</p>
                <ul>
                  <li>Start Storm Mode</li>
                  <li>View Routes</li>
                  <li>View Customers (not shown to drivers)</li>
                </ul>
              </AccordionSection>

              <AccordionSection id="gs-navigation" title="Navigation">
                <h4>Main Menu Items</h4>
                <ul>
                  <li><strong>Dashboard</strong> - Business overview and weather</li>
                  <li><strong>Customers</strong> - Manage customer accounts and properties</li>
                  <li><strong>Routes</strong> - Create and manage service routes</li>
                  <li><strong>History</strong> - View completed services and payments (owner and admin)</li>
                  <li><strong>My History</strong> - Your own routes and pay (crew leads and drivers)</li>
                  <li><strong>Expenses</strong> - Track business expenses (Pro tier)</li>
                  <li><strong>Reports</strong> - Profit & Loss analysis (Pro tier)</li>
                  <li><strong>Storm Mode</strong> - Quick storm service management</li>
                  <li><strong>Settings</strong> - Company and account configuration</li>
                  <li><strong>?</strong> - Help Center (you're here!)</li>
                </ul>

                <h4>Mobile Navigation</h4>
                <p>On mobile devices, tap the menu icon (three lines) to access the full navigation menu.</p>
              </AccordionSection>

              <AccordionSection id="gs-mobile" title="Add SnowTrack™ to your home screen">
                <p>Put SnowTrack™ on your phone's home screen so it opens like an app in the truck, full screen and without the browser bar.</p>

                <h4>iPhone</h4>
                <ol>
                  <li>Open <strong>snowtrack.pro</strong> in <strong>Safari</strong></li>
                  <li>Tap the <strong>Share</strong> button (the square with an arrow pointing up)</li>
                  <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
                  <li>Tap <strong>Add</strong></li>
                </ol>

                <h4>Android</h4>
                <ol>
                  <li>Open <strong>snowtrack.pro</strong> in <strong>Chrome</strong></li>
                  <li>Tap the menu (the three dots, top right)</li>
                  <li>Tap <strong>Add to Home screen</strong> or <strong>Install app</strong></li>
                  <li>Tap <strong>Install</strong> or <strong>Add</strong> to confirm</li>
                </ol>

                <p>Open SnowTrack™ from the new icon. You may need to log in once the first time you open it that way.</p>

                <h4>Mobile Features</h4>
                <ul>
                  <li><strong>Pull-to-refresh</strong> - Pull down on any page to refresh data</li>
                  <li><strong>Update bar</strong> - When a new version is ready, a bar at the bottom reads
                  &quot;A new version of SnowTrack is ready. Tap to update.&quot; The app checks every time you open
                  it or come back to it, and nothing reloads until you tap the bar</li>
                  <li><strong>Tap phone numbers</strong> - Quick call or text customers</li>
                  <li><strong>Touch-friendly interface</strong> - Large buttons</li>
                  <li><strong>Loads without a connection</strong> - the app itself is cached on your phone, so it opens when you have no signal. Your customers, routes and photos still need a connection to load or save.</li>
                </ul>
              </AccordionSection>

              <div className="steps-grid">
                <div className="step-card">
                  <div className="step-number">1</div>
                  <h3>Add Your Customers</h3>
                  <p>Start by adding your customers with their service addresses and pricing information.</p>
                  {!isDriver() && (
                    <button className="btn btn-sm btn-primary" onClick={() => navigate('/customers')}>
                      Go to Customers
                    </button>
                  )}
                </div>

                <div className="step-card">
                  <div className="step-number">2</div>
                  <h3>Set Up Pricing Tiers</h3>
                  <p>Configure your pricing tiers (e.g., Small, Medium, Large) with base and salt prices.</p>
                  <button className="btn btn-sm btn-primary" onClick={() => navigate('/settings')}>
                    Go to Settings
                  </button>
                </div>

                <div className="step-card">
                  <div className="step-number">3</div>
                  <h3>Create Routes</h3>
                  <p>Organize customers into routes for efficient service during storms.</p>
                  <button className="btn btn-sm btn-primary" onClick={() => navigate('/routes')}>
                    Go to Routes
                  </button>
                </div>

                <div className="step-card">
                  <div className="step-number">4</div>
                  <h3>Use Storm Mode</h3>
                  <p>When it snows, use Storm Mode to quickly service your routes and track payments.</p>
                  <button className="btn btn-sm btn-primary" onClick={() => navigate('/storm-mode')}>
                    Go to Storm Mode
                  </button>
                </div>
              </div>

              <div className="tip-box">
                <strong>Pro Tip:</strong> Set up your invoice defaults in Settings before your first storm.
                This allows you to send professional invoices with one click using the Bill All feature.
              </div>
            </div>
          )}

          {/* Features Tab */}
          {activeTab === 'features' && (
            <div className="tab-content">
              <h2 className="section-title">Feature Guide</h2>

              <AccordionSection id="customers" title="Customer Management">
                <h4>Adding Customers</h4>
                <ol>
                  <li>Navigate to the <strong>Customers</strong> page</li>
                  <li>Click <strong>+ Add Customer</strong></li>
                  <li>Fill in the customer form (see fields below)</li>
                  <li>Click <strong>Add Customer</strong></li>
                </ol>

                <h4>Customer Fields Explained</h4>
                <ul>
                  <li><strong>First Name / Last Name</strong> - Required for Square invoicing</li>
                  <li><strong>Email</strong> - Required for sending invoices</li>
                  <li><strong>Phone</strong> - Tap to call or text</li>
                  <li><strong>Service Address</strong> - Where you provide service</li>
                  <li><strong>Billing Address</strong> - Only if different from service address</li>
                  <li><strong>Service Type</strong> - Snow Removal, Lawn Care, or Both Services. Set it on the customer or on an individual property; LawnTrack™ arrives Spring 2027</li>
                  <li><strong>Pricing Tier</strong> - Select from your configured tiers, or Custom</li>
                  <li><strong>Base Price / Salt Price</strong> - Filled in from the tier; change them for this customer if needed</li>
                  <li><strong>Service Preference</strong> - Seasonal or On-Call / As Needed, and the snowfall that triggers service</li>
                  <li><strong>Services Included</strong> - Driveway, Sidewalks, Walkways</li>
                  <li><strong>Salt Preference</strong> - No Salt, Always Salt, or Salt As Needed (chosen at each visit in Storm Mode)</li>
                  <li><strong>Lead Source</strong> - How they found you (Facebook, Google, Referral, etc.)</li>
                  <li><strong>Referred By</strong> - Select referring customer for referral discounts</li>
                  <li><strong>Notes</strong> - Special instructions or gate codes</li>
                  <li><strong>Subscribed to emails</strong> - Whether the customer receives your bulk emails (Pro)</li>
                </ul>

                <h4>Pricing Tiers vs Custom Pricing</h4>
                <p><strong>Pricing Tiers</strong> are predefined price categories you set up in Settings:</p>
                <ul>
                  <li>Each tier has a name, base price, and salt price</li>
                  <li>Select a tier and prices auto-populate</li>
                  <li>Great for standardized pricing</li>
                </ul>
                <p><strong>Custom Pricing</strong> overrides the tier:</p>
                <ul>
                  <li>Choose <strong>Custom</strong> in the Tier dropdown, or edit the prices the tier filled in</li>
                  <li>Enter specific base and salt prices for this customer</li>
                  <li>Useful for negotiated rates or special circumstances</li>
                </ul>

                <h4>Multiple Properties</h4>
                <p>Customers can have multiple service addresses:</p>
                <ol>
                  <li>Find the customer's card</li>
                  <li>Click <strong>+ Property</strong></li>
                  <li>Enter property address and pricing</li>
                  <li>Each property can have its own tier, custom pricing, and salt preference</li>
                </ol>

                <h4>Signups Held by Your Plan Limit</h4>
                <p>If someone signs up through your public intake form while you are at your plan's
                customer limit, the signup is kept for you instead of being turned away:</p>
                <ul>
                  <li>A banner at the top of the Customers page tells you how many are held.</li>
                  <li>Each one carries a <strong>Held: plan limit reached</strong> badge.</li>
                  <li>Held signups do not count against your limit.</li>
                  <li><strong>Activate</strong> takes one on, together with its properties, once you
                  have room. Make room by upgrading your plan or deactivating a customer you no longer
                  serve.</li>
                </ul>

                <h4>Do Not Serve and Size Confirmation</h4>
                <ul>
                  <li><strong>Do Not Serve</strong> - Tick it on the Edit form, with an optional reason that
                  only owners and admins can see. Adding the customer to a route then asks you to confirm.</li>
                  <li><strong>Size unconfirmed</strong> - Shown when a customer picked their own driveway
                  size on your signup form. Click Confirm beside it to accept the size shown, or choose a
                  tier on the card or save it in Edit.</li>
                  <li><strong>Welcome message</strong> - Confirming the size of a signup from your form sends
                  them one welcome: by email, and by text too if they agreed to texts and texting is set up.
                  It is not sent twice, not to a Do Not Serve customer, and not when you have turned it off.
                  The card then shows <strong>Welcomed</strong> with the date and how it went.
                  <strong> Send welcome</strong> on the card sends it yourself, for example to a customer you
                  added by hand; on someone already welcomed it says <strong>Send welcome again</strong>.</li>
                  <li><strong>New - From Form</strong> - Shown on a signup until you review it: save its Edit
                  form, or press Mark reviewed on the Dashboard card.</li>
                  <li><strong>New signups on this page</strong> - Owners and admins see the Dashboard's
                  new-signups card above the search box too; Review scrolls to the customer's card, unreviewed
                  signups sort to the top, and New signups only shows just them.</li>
                  <li><strong>Salt on signups</strong> - Your form and the season confirm page offer Always (salt every visit), When icy and No; they come in as Always Salt, Salt As Needed and No Salt.</li>
                </ul>

                <h4>Season Confirmations</h4>
                <p>Track who has answered your season email: confirmed, declined, or no reply yet.</p>
                <ul>
                  <li><strong>Start</strong> - On the Dashboard, the Season tile says <strong>Set up the
                  season</strong>. Start adds every active customer as No reply; Do Not Serve and inactive
                  customers are left out. Between seasons the tile is about the next season.</li>
                  <li><strong>Recording an answer</strong> - On a No reply customer, click
                  <strong> Confirmed</strong> (choose Seasonal or On-call, salt, and size, then Save) or
                  <strong> Declined</strong>. Confirmed updates the card with those choices; a different size
                  marks it Size unconfirmed for you to check. <strong>Undo</strong> in the card's buttons
                  puts it back to No reply.</li>
                  <li><strong>Yes and No links</strong> - Put {'{{season_yes_link}}'} and {'{{season_no_link}}'} in a
                  bulk email or text; pick <strong>No reply this season</strong> to send only to those who have not
                  answered. Each customer gets their own links, the same all season.</li>
                  <li><strong>The customer's page</strong> - No login. No records Declined at once; Yes shows their
                  service, salt and size filled in from the card, and Confirm records Confirmed with those choices.
                  Each answer is emailed to the owner, or to your business email if the owner has none.</li>
                  <li><strong>Remind</strong> - On the Season tile, Remind shows how many it will text, email and
                  skip, then sends your Season check-in message (Settings) to every No reply customer: a text if they
                  agreed to texts and texting is on, otherwise an email. When it includes texts, Remind is refused outside quiet hours (8:00 AM to 9:00 PM) and nothing is queued; try again later. Within 48 hours of
                  the last reminder it asks you to Send anyway. "Last reminded" shows under the counts.</li>
                  <li><strong>Schedule</strong> - Beside Remind, Schedule sends the same reminder later: pick a date
                  and time, in your company's time zone, up to 30 days ahead. A time outside 8:00 AM to 9:00 PM is
                  refused when the reminder includes texts (an email-only reminder can go at any hour), and so is a
                  time within 48 hours of the last reminder; the message says the next time that works. A reminder that
                  emails anyone needs your business address in Settings before it can be scheduled. The picker opens
                  filled in with 8:00 AM tomorrow (or the first time after the 48 hours). The tile shows
                  "Scheduled for" the time and who set it, with Cancel; picking a new time replaces it. At that time
                  it sends exactly as Remind would and the tile shows what was sent. If it cannot be sent then (for
                  example the customers were reminded in the meantime, or the business address is missing), it is not
                  tried again: the tile says why, and you can schedule again. A scheduled reminder more than 12 hours
                  late is not sent.</li>
                  <li><strong>Intake signups</strong> - A signup during the season comes in Confirmed.</li>
                  <li><strong>Filter and print</strong> - The Season filter shows All, Confirmed, Declined or
                  No reply, and Print List prints what the filter shows.</li>
                  <li><strong>Routes</strong> - Customers who declined are hidden from Available; tick Show declined to
                  see them. Add all skips them and says how many. Adding one with + Add still works, with a warning.</li>
                </ul>

                <h4>Discounts</h4>
                <p>Apply discounts to individual customers:</p>
                <ul>
                  <li><strong>Percentage Discount</strong> - e.g., 10% off (applied to total)</li>
                  <li><strong>Dollar Discount</strong> - e.g., $5 off per service</li>
                  <li><strong>Discount Reason</strong> - Add a note explaining the discount</li>
                </ul>

                <h4>Lead Sources & Referrals</h4>
                <ul>
                  <li><strong>Lead Source</strong> - Track how customers find you (Facebook, Google, Yard Sign, Referral, etc.)</li>
                  <li><strong>Referred By</strong> - Link to the customer who referred them for referral discount tracking</li>
                </ul>

                <h4>Editing & Deleting Customers</h4>
                <ul>
                  <li><strong>Edit</strong> - Click Edit on any customer card</li>
                  <li><strong>Deactivate</strong> - Hide a customer without deleting history; Reactivate brings them back</li>
                  <li><strong>Delete</strong> - Permanent; not allowed while the customer is still on a route</li>
                </ul>

                <h4>Print Customer List</h4>
                <ol>
                  <li>Go to the <strong>Customers</strong> page</li>
                  <li>Click the <strong>Print List</strong> button (printer icon)</li>
                  <li>A formatted list opens with company header, customer details, and property info</li>
                </ol>

                <h4>Message Customers (Pro)</h4>
                <ol>
                  <li>On the <strong>Customers</strong> page click <strong>Message Customers</strong> (owner and admin)</li>
                  <li>Choose who receives it. In <strong>Manual selection</strong>, customers with no email address or who are unsubscribed are greyed out and never included</li>
                  <li>An unsubscribed customer who has an email address shows a <strong>Subscribe</strong> button. Use it only when the customer has asked to receive your emails again: it saves them as subscribed, the same as ticking <strong>Subscribed to emails</strong> on their edit form, and they become selectable right away</li>
                  <li>Write the email or use a template, preview it, and send. The heading then reads <strong>Sent: N emails</strong> (with texts too, <strong>Sent: N emails, N texts</strong>)</li>
                </ol>
                <p>Every bulk email and season check-in email ends with a footer SnowTrack adds: why the
                customer gets it, an <strong>Unsubscribe</strong> link, your company name and phone, and your
                business address. The law requires a postal address on marketing email, so sending is refused
                until <strong>Business Address</strong> is set in Settings &gt; Company Information (a PO box is
                fine); the window and the preview say so. A customer who unsubscribes is never emailed again
                until you tick <strong>Subscribed to emails</strong> for them, and their card reads
                {' '}<strong>Unsubscribed from email on</strong> the date. Turn it back on only if they ask.</p>
                <p>Insert buttons add fields that are filled in separately for each recipient, in the
                subject and the body: <code>{'{{customer_name}}'}</code>, <code>{'{{property_address}}'}</code>,
                {' '}<code>{'{{company_name}}'}</code>, <code>{'{{amount_due}}'}</code> (their current balance; a
                credit reads $0.00 and visits not yet priced are named), <code>{'{{driveway_size}}'}</code>,
                {' '}<code>{'{{snow_price}}'}</code> (with depth pricing, their size's price in each band),
                {' '}<code>{'{{salt_price}}'}</code>, <code>{'{{service_preference}}'}</code>,
                {' '}<code>{'{{rates_link}}'}</code> (your public rates page) and <code>{'{{portal_link}}'}</code>
                {' '}(the portal login, or a fresh setup link for a customer with an unaccepted invite). The
                preview shows one real recipient exactly as their email will be sent. The Payment Reminder
                template puts the amount due in the subject and the portal link in the body.</p>

                <h4>Customer Portal Invites</h4>
                <ul>
                  <li><strong>Invite to Portal</strong> on a customer's card sends one invite (owner and admin)</li>
                  <li><strong>Invite all to portal</strong> on the Customers page invites every active customer
                  who has an email and no portal account, one at a time, and reports Sent and Skipped. A
                  customer with an unaccepted invite gets a new one and the old link stops working</li>
                  <li>A customer with accounts at more than one company picks the company after entering a
                  correct password. Forgot password sends one reset email per company, each naming it</li>
                  <li>On an iPhone or Android phone, the portal&apos;s Dashboard shows your customer how to add
                  it to their home screen. The icon is named <strong>My Snow Service</strong></li>
                </ul>

                <h4>Phone Actions</h4>
                <p>Tap a phone number on a customer card to call. In Storm Mode, tapping a number gives you:</p>
                <ul>
                  <li><strong>Call</strong> - Start a phone call</li>
                  <li><strong>Text</strong> - Open SMS message</li>
                </ul>

                <h4>Depth Pricing on Customers</h4>
                <ul>
                  <li><strong>On-call badge:</strong> with depth pricing on and an on-call premium above 0, on-call
                  customers show "On-call +20%" (your percentage) in the list and on the edit form. Changing it
                  never changes a completed stop.</li>
                  <li><strong>Balances:</strong> a card shows "N awaiting depth" for completed stops with no price
                  yet. They are not part of what is owed.</li>
                  <li><strong>Statements:</strong> those stops are listed as <strong>Awaiting depth</strong> with no
                  charge, and the statement, printed copy included, says "This statement includes N stops waiting
                  for a storm depth; their charges are not included." Priced rows show the band and any on-call
                  premium.</li>
                  <li><strong>Customer portal:</strong> a visit waiting for the depth shows "Price pending storm
                  total" with no Pay Now; priced visits show the band and any on-call premium.</li>
                </ul>
              </AccordionSection>

              <AccordionSection id="routes" title="Routes">
                <h4>Creating a Route</h4>
                <ol>
                  <li>Navigate to <strong>Routes</strong></li>
                  <li>Click <strong>+ New Route</strong> (owner and admin)</li>
                  <li>Enter a descriptive route name (e.g., "North Side", "Downtown")</li>
                  <li>Enter a <strong>Storm ID</strong> (e.g., the date) and choose who it is <strong>Assigned To</strong>, with optional driver pay</li>
                  <li>Optionally set starting and ending addresses for navigation</li>
                  <li>Configure invoice settings if using Square invoicing</li>
                  <li>Click <strong>Create Route</strong></li>
                </ol>

                <h4>Adding Stops</h4>
                <ol>
                  <li>Click <strong>Stops</strong> on the route card</li>
                  <li>Click <strong>+ Add</strong> beside each customer you want</li>
                  <li>For multi-property customers, each property appears as a separate stop</li>
                </ol>

                <h4>Ordering Stops</h4>
                <p>Optimize your route order:</p>
                <ul>
                  <li>Click <strong>Optimize Route</strong> (2 or more stops) to put the stops in the best driving order</li>
                  <li>In Storm Mode, <strong>Re-optimize Remaining</strong> reorders the stops still to do from where you are</li>
                </ul>

                <h4>Starting/Ending Locations</h4>
                <p>Optionally set addresses where you start and end:</p>
                <ul>
                  <li><strong>Starting Address</strong> - Where you begin (e.g., your shop)</li>
                  <li><strong>Ending Address</strong> - Where you finish</li>
                  <li>These appear in Google Maps navigation if configured</li>
                </ul>

                <h4>Invoice Settings per Route</h4>
                <p>Customize invoices for each route:</p>
                <ul>
                  <li><strong>Invoice Title</strong> - Custom title using variables:
                    <ul>
                      <li>{'{route_name}'} - Route name</li>
                      <li>{'{service_date}'} - Date of service</li>
                      <li>{'{customer_name}'} - Customer name</li>
                    </ul>
                  </li>
                  <li><strong>Invoice Message</strong> - Personal message on invoices</li>
                </ul>
                <p>If not set, company-wide defaults from Settings are used.</p>

                <h4>Route Statuses</h4>
                <ul>
                  <li><strong>Not Started</strong> - Route created, ready to begin</li>
                  <li><strong>In Progress</strong> - Currently being serviced</li>
                  <li><strong>Completed</strong> - All stops finished, ready for billing</li>
                  <li><strong>Archived</strong> - Historical record, hidden from active view</li>
                </ul>

                <h4>Payment Status Display</h4>
                <p>Completed route cards show a payment summary:</p>
                <ul>
                  <li><strong>✓ Paid</strong> - Every stop is paid</li>
                  <li><strong>$X unpaid</strong> - The amount still owed on the route</li>
                </ul>

                <h4>Viewing & Editing Routes</h4>
                <ul>
                  <li><strong>Click route card</strong> - View route details and stops</li>
                  <li><strong>Edit button</strong> - Modify route name, addresses, invoice settings (owner and admin)</li>
                  <li><strong>Archive button</strong> - Move to archived routes (owner and admin, here and in Storm Mode)</li>
                  <li><strong>Delete</strong> - Only for routes with no completed stops; otherwise you are offered Archive Instead</li>
                </ul>

                <h4>Sorting Routes</h4>
                <p>Toggle sort order using the sort button:</p>
                <ul>
                  <li><strong>↓ Newest</strong> - Most recently created at top</li>
                  <li><strong>↑ Oldest</strong> - Oldest routes at top</li>
                </ul>
              </AccordionSection>

              <AccordionSection id="storm-mode" title="Storm Mode">
                <h4>What is Storm Mode?</h4>
                <p>Storm Mode is designed for quick, efficient service during active storms. It streamlines
                the process of completing stops, tracking payments, and billing customers.</p>

                <h4>Starting a Route in Storm Mode</h4>
                <ol>
                  <li>Click <strong>Storm Mode</strong> in navigation</li>
                  <li>Tap a route. There is no separate storm to create: a storm is the routes that share a Storm ID</li>
                  <li>For a route that has not started, choose <strong>Saved location</strong> or <strong>My current GPS location</strong> as your starting point</li>
                  <li>The <strong>Up Next</strong> card shows the next stop, with <strong>Done</strong>, <strong>Navigate</strong> and <strong>Skip</strong></li>
                </ol>

                <h4>Completing a Stop</h4>
                <p>When you arrive at a customer, tap <strong>Done</strong>, then:</p>
                <ol>
                  <li>Optionally <strong>Take photo</strong> (your customer sees it in their portal)</li>
                  <li>Tick <strong>Apply Salt</strong> if shown (Salt As Needed customers only; Always Salt and No Salt are automatic)</li>
                  <li>Tick any <strong>Add-on Services</strong>, and add a discount or extra charge if needed</li>
                  <li>Choose how the customer is paying (see Payment Options below). This completes the stop and locks its price</li>
                </ol>

                <h4>Skipping a Stop</h4>
                <p>If a customer doesn't need service:</p>
                <ol>
                  <li>Tap <strong>Skip</strong> on the stop</li>
                  <li>The stop is marked as skipped (no charge)</li>
                </ol>

                <h4>The Importance of Storm ID</h4>
                <div className="note-box">
                  <strong>Key Point:</strong> Storm ID is a label you set on each route, such as the date.
                  Give every route in the same storm the same Storm ID: <strong>referral discounts</strong> only
                  apply when both referrer and referred customer are serviced on routes with the same Storm ID.
                </div>

                <h4>Payment Options</h4>
                <p>Select how the customer will pay. Only the methods switched on in Settings appear:</p>
                <ul>
                  <li><strong>Venmo, Cash, Cash App, Zelle, Check - Paid</strong>, and any method you added - Payment received</li>
                  <li><strong>Venmo, Cash, Cash App, Zelle, Check - Not Paid</strong>, and any method you added - Customer will pay that way later</li>
                  <li><strong>Not Paid</strong> - Leave unpaid to collect later</li>
                  <li><strong>Square</strong> (under Send Invoice) - Bill by Square invoice. No card is charged at the stop. With <strong>Send the Square invoice automatically when a stop is completed</strong> on in Settings, the invoice is emailed when the stop is completed; otherwise send it later with Bill All</li>
                  <li><strong>Mark as Comp/Free</strong> - No charge, with a reason</li>
                </ul>
                <p>A payment method you add yourself in Settings gets its own Paid and Not Paid buttons,
                like the built-in ones, once it is switched on.</p>

                <h4>Telling the Customer You Are On the Way (Pro)</h4>
                <p>With the <strong>Storm Mode arrival alert</strong> on (Settings, Texting and Email), the
                customer gets a text if they agreed to texts, otherwise an email if they have an address and
                are subscribed. It goes out when the driver taps <strong>Navigate</strong> or <strong>On my
                way</strong>, and when the driver completes or skips a stop on a route from today or
                yesterday. Each stop gets it once.</p>
                <ul>
                  <li><strong>Text customers when the driver is</strong> - Next stop only (the default), Next 2
                  stops or Next 3 stops</li>
                  <li><strong>{'{{stops_away}}'}</strong> - In the message, becomes &quot;1 stop&quot;, &quot;2 stops&quot; or
                  &quot;3 stops&quot;. The default text is &quot;{'{{company_name}}'}: we&apos;re about {'{{stops_away}}'} away
                  from {'{{property_address}}'}. Reply STOP to opt out.&quot;</li>
                  <li>The stop then shows <strong>Texted</strong> or <strong>Emailed</strong> with the time,
                  <strong> Not notified</strong> with the reason, or <strong>Notify failed</strong> (not
                  retried)</li>
                </ul>

                <h4>Bill All Feature (Pro)</h4>
                <p>After completing a route, bill all unpaid Square customers at once:</p>
                <ol>
                  <li>Complete all stops on the route</li>
                  <li>Click <strong>Bill All</strong> on the completed route in Storm Mode</li>
                  <li>Review the list, then click <strong>Bill All</strong> or <strong>Bill Selected</strong></li>
                  <li>Square invoices are sent for unpaid stops where you chose Square, for customers with email addresses</li>
                </ol>

                <h4>Print Summary</h4>
                <p>Print a route summary showing:</p>
                <ul>
                  <li>Route name and date</li>
                  <li>All stops with service details</li>
                  <li>Payment status for each stop</li>
                  <li>Total revenue</li>
                  <li>On depth routes: "Depth: 6.5 in (source)" or "Depth: not entered" in the header, each
                  stop's band, and stops awaiting depth printed as Awaiting depth and left out of the totals</li>
                </ul>

                <h4>Route Completion</h4>
                <p>When all stops are complete or skipped:</p>
                <ol>
                  <li>The route automatically shows as "Completed"</li>
                  <li>Use <strong>Bill All</strong> to invoice remaining unpaid stops</li>
                  <li>Archive the route when fully processed (owner and admin)</li>
                </ol>

                <div className="note-box">
                  <strong>Note:</strong> On a free trial, the first stop you complete starts your
                  trial storm, which lasts 72 hours. A route started inside those 72 hours can always
                  be finished. On a paid plan there is no storm limit at all, because plans are flat
                  monthly and storm events are unlimited.
                </div>
              </AccordionSection>

              <AccordionSection id="billing" title="Billing & Payments">
                <h4>Square Integration</h4>
                <p>Square invoicing allows you to send professional invoices directly from SnowTrack™:</p>
                <ul>
                  <li>Invoices are sent from your own Square account, with your business info</li>
                  <li>Customers receive email with payment link</li>
                  <li>Accept credit/debit cards online</li>
                  <li>Payment status syncs back to SnowTrack™</li>
                </ul>
                <p><strong>Setup:</strong> Connect your own Square account in <strong>Settings →
                Invoicing and Payments → Square Account</strong>. Click <strong>Connect Square</strong>, sign in
                to Square, and pick a location if your account has more than one. Invoices are then
                created in your Square account and the money goes to you.{' '}
                <strong>Disconnect</strong>, in the same section, revokes access.</p>

                <h4>Bill All Feature</h4>
                <p>Bill all unpaid Square customers on a route at once (Pro tier):</p>
                <ol>
                  <li>Complete all stops on the route</li>
                  <li>Click <strong>Bill All</strong> on the completed route in Storm Mode</li>
                  <li>Review the list of customers to be invoiced</li>
                  <li>Confirm to send all invoices</li>
                </ol>

                <h4>Invoice Line Items</h4>
                <p>Each invoice includes separate line items:</p>
                <ul>
                  <li><strong>Snow Removal</strong> - Base service price, plus any extra charge</li>
                  <li><strong>Salt Application</strong> - Salt price (if salt was applied)</li>
                  <li>One line for each add-on service</li>
                  <li>Customer discounts are applied; referral discounts are not</li>
                </ul>

                <h4>Invoice Title & Message Customization</h4>
                <p>Personalize invoices using variables:</p>
                <ul>
                  <li>{'{route_name}'} - Name of the route</li>
                  <li>{'{service_date}'} - Date service was performed</li>
                  <li>{'{customer_name}'} - Customer's name</li>
                </ul>
                <p>Set defaults in Settings, or customize per route.</p>

                <h4>Customer Requirements for Invoicing</h4>
                <p>Customers must have:</p>
                <ul>
                  <li><strong>First and Last Name</strong> - Required for Square customer creation</li>
                  <li><strong>Email Address</strong> - Where invoice is sent</li>
                </ul>
                <p>Customers missing these fields will be skipped during Bill All.</p>

                <h4>Payment Status Tracking</h4>
                <p>Each service has a payment status:</p>
                <ul>
                  <li><strong>Not Paid</strong> - Awaiting payment</li>
                  <li><strong>Paid</strong> - Payment received, shown with the method</li>
                  <li><strong>Invoice Sent</strong> - A Square invoice is out; it turns Paid on its own when the customer pays</li>
                </ul>

                <h4>Manual Payment Methods</h4>
                <p>For payments received outside Square:</p>
                <ul>
                  <li><strong>Venmo</strong> - Customer paid via Venmo</li>
                  <li><strong>Cash</strong> - Cash payment received</li>
                  <li><strong>Cash App</strong> - Cash App payment</li>
                  <li><strong>Check</strong> - Check payment</li>
                  <li><strong>Zelle</strong> - Zelle transfer</li>
                  <li>Any payment method you add in Settings, by the name you gave it</li>
                </ul>
                <p>Mark as paid in Storm Mode when completing stops, or later in Service History.</p>
              </AccordionSection>

              <AccordionSection id="texting" title="Texting (Pro)">
                <h4>Setting Up Texting</h4>
                <p>Go to <strong>Settings → Texting and Email → How texts are sent</strong>. Owners and
                admins see the card on the Pro plan and up when texting is available. Texts only go to
                customers who agreed to receive them. Choose a mode:</p>
                <ul>
                  <li><strong>Off</strong> - No texts are sent</li>
                  <li><strong>SnowTrack number</strong> - SnowTrack provides a local number and handles
                  carrier registration. Only the owner can click <strong>Set up my texting number</strong>,
                  which shows the cost before anything is charged. The owner then enters the business
                  details the carriers need, and the card shows each step until the number is approved</li>
                  <li><strong>My own Twilio</strong> - Texts go out through your own Twilio account: enter the
                  Account SID, Auth Token and Messaging Service SID</li>
                </ul>
                <p>The owner can click <strong>Send a test text to my phone</strong> to check it works (up
                to 5 an hour).</p>

                <h4>What Texting Costs</h4>
                <ul>
                  <li><strong>Setup fee</strong> - One time, $25 by default, charged to the card on your
                  subscription when the owner sets up a SnowTrack number</li>
                  <li><strong>Texting-number line</strong> - $5 a month by default, added to your
                  subscription once carriers approve your number</li>
                  <li><strong>After your plan ends</strong> - Your number is kept for 30 days and texts
                  stop. The card shows the release date, and the owner is emailed once. Resubscribe before
                  then to keep it; after that date the number is released and the monthly line comes off</li>
                  <li><strong>Reinstatement</strong> - After a release, <strong>Set up a new texting
                  number</strong> charges a one-time reinstatement fee, $25 by default, instead of the setup
                  fee. If the card says there is no reinstatement fee, nothing is charged</li>
                </ul>
                <p>These are the standard amounts; the card always shows the amount it will charge.</p>

                <h4>Your Policy Pages</h4>
                <p>Carriers need a texting privacy policy and terms for your business. Leave both blank and
                SnowTrack™ hosts them for you at snowtrack.pro/sms-policy/your-company/privacy and /terms.
                If your website has its own, the owner enters both under <strong>Your policy pages</strong>:
                both or neither, each starting https://. They can be changed until your texting campaign
                goes to the carriers for review, or after the carriers reject it.</p>

                <h4>Sending Texts</h4>
                <ul>
                  <li><strong>Bulk texts</strong> - Message Customers, then Send as Text (or Both).
                  &quot;Reply STOP to opt out.&quot; is added for you</li>
                  <li><strong>Quiet hours</strong> - Bulk texts go out from 8:00 AM to 9:00 PM in your
                  company&apos;s time zone only. Arrival alerts, skip notices and texts to one customer are not held</li>
                  <li><strong>STOP and HELP</strong> - A customer who replies STOP is never texted again unless
                  they reply START. HELP gets a reply with your company name</li>
                  <li><strong>Replies</strong> - Emailed to your business email and kept on the customer card
                  under Texts</li>
                </ul>
                <p>For the arrival alert ({'{{stops_away}}'}, Next stop only, Next 2 stops or Next 3 stops), see
                {' '}<em>Telling the Customer You Are On the Way</em> under Storm Mode.</p>
                <p><strong>When an alerted stop is skipped</strong> (under the arrival alert in Settings, on unless
                you turn it off): if a driver skips a stop after its customer got the arrival alert, the
                customer gets one follow-up straight away, the same way the alert went: a text after a text,
                an email after an email. The default is &quot;{'{{company_name}}'}: we had to skip
                {' '}{'{{property_address}}'} today. We&apos;ll be back as soon as we can. Reply STOP to opt
                out.&quot; It can use {'{{customer_name}}'}, {'{{property_address}}'} and {'{{company_name}}'}, not
                {' '}{'{{stops_away}}'}. Each stop gets it once, even if it is un-skipped and skipped again;
                a stop never alerted, a route older than yesterday, or a customer who has since opted out
                gets nothing. The skipped stop shows <strong>Skip texted</strong> or <strong>Skip
                emailed</strong> with the time, or <strong>Skip notice failed</strong> (not retried).</p>
              </AccordionSection>

              <AccordionSection id="service-history" title="Service History">
                <h4>Viewing History</h4>
                <p>The Service History page (owner and admin) shows all completed services. Filter by:</p>
                <ul>
                  <li>Date range</li>
                  <li>Customer</li>
                  <li>Route</li>
                  <li>Payment status</li>
                </ul>
                <p>Crew leads and drivers use <strong>My History</strong>, which shows their own routes and pay.</p>

                <h4>Payment Tracking</h4>
                <ul>
                  <li><strong>Not Paid</strong> - Service completed, awaiting payment</li>
                  <li><strong>Paid</strong> - Payment received</li>
                  <li><strong>COMP</strong> - Done free of charge</li>
                </ul>

                <h4>Recording Payments</h4>
                <ol>
                  <li>Find the service in history</li>
                  <li>Click <strong>Mark Paid</strong></li>
                  <li>Select payment method</li>
                  <li>Check the <strong>Payment Date</strong>: it starts at today in your company&apos;s time zone, and you can change it</li>
                </ol>
              </AccordionSection>

              <AccordionSection id="expenses" title="Expenses (Pro Tier)">
                <p><em>Available on Pro tier and above.</em></p>

                <h4>Adding Expenses</h4>
                <ol>
                  <li>Navigate to <strong>Expenses</strong></li>
                  <li>Click <strong>+ Add Expense</strong></li>
                  <li>Fill in expense details:
                    <ul>
                      <li><strong>Date</strong> - When the expense occurred</li>
                      <li><strong>Amount</strong> - Cost of the expense</li>
                      <li><strong>Category</strong> - Type of expense</li>
                      <li><strong>Description</strong> - Details about the expense</li>
                      <li><strong>Route</strong> - Optionally link to a specific route</li>
                    </ul>
                  </li>
                  <li>Click <strong>Add & Close</strong></li>
                </ol>

                <h4>Quick Multi-Entry</h4>
                <p>Quickly add multiple expenses:</p>
                <ul>
                  <li><strong>Add & Add Another</strong> - Save and keep form open</li>
                  <li><strong>Add & Close</strong> - Save and close form</li>
                  <li><strong>Update & Add New</strong> - When editing, save and start a new expense</li>
                </ul>

                <h4>Expense Categories</h4>
                <p>New accounts start with no categories: add your own in <strong>Settings → Invoicing
                and Payments → Expense Categories</strong> before your first expense. Accounts created
                before categories were introduced also have these built-in categories:</p>
                <ul>
                  <li><strong>Labor</strong> - Payments to helpers or contractors</li>
                  <li><strong>Materials</strong> - Sand and supplies</li>
                  <li><strong>Fuel</strong> - Gas, diesel for trucks and equipment</li>
                  <li><strong>Equipment</strong> - Tools, plows, blowers, parts</li>
                  <li><strong>Repairs</strong> - Maintenance and repair costs</li>
                  <li><strong>Salt</strong> - Salt bought for the season</li>
                  <li><strong>Other</strong> - Miscellaneous business expenses</li>
                </ul>
                <p>Every account gets <strong>Processing Fees</strong> automatically the first time a
                Square payment fee is recorded.</p>
                <p>Add your own categories in <strong>Settings → Invoicing and Payments → Expense
                Categories</strong>. Built-in categories can be switched off but not deleted;
                categories you added yourself can be deleted.</p>

                <h4>Linking to Routes</h4>
                <p>Link expenses to specific routes for better tracking:</p>
                <ul>
                  <li>Select a route when adding an expense</li>
                  <li>Expenses show up in route profitability calculations</li>
                  <li>Filter expenses by route to see costs per job</li>
                </ul>

                <h4>Filtering & Summary</h4>
                <p>Filter your expense list:</p>
                <ul>
                  <li><strong>By Category</strong> - See only specific types</li>
                  <li><strong>By Route</strong> - Expenses for a specific route</li>
                  <li><strong>By Date Range</strong> - Custom start and end dates</li>
                </ul>
                <p>Summary section shows totals by category, updating as you filter.</p>
              </AccordionSection>

              <AccordionSection id="reports" title="Reports (Pro Tier)">
                <p><em>Available on Pro tier and above.</em></p>

                <h4>Accessing Reports</h4>
                <ol>
                  <li>Navigate to <strong>Reports</strong> from the main menu</li>
                  <li>Select a date range preset or custom dates</li>
                  <li>View your Profit & Loss analysis</li>
                </ol>

                <h4>Date Range Options</h4>
                <ul>
                  <li><strong>This Month</strong> - Current calendar month</li>
                  <li><strong>Last Month</strong> - Previous calendar month</li>
                  <li><strong>This Season</strong> - Your season from Settings &gt; Company &gt; Season (October - April unless you change it)</li>
                  <li><strong>Custom</strong> - Select specific start and end dates</li>
                </ul>

                <h4>Revenue Breakdown</h4>
                <p>View revenue split by:</p>
                <ul>
                  <li><strong>Payment Status</strong> - Paid vs Unpaid amounts</li>
                  <li><strong>Payment Method</strong> - Square, Venmo, Cash, Cash App, Check, Zelle, and any you add in Settings</li>
                </ul>

                <h4>Expense Breakdown</h4>
                <p>View expenses totaled by category:</p>
                <ul>
                  <li>Each of your expense categories, built-in or added by you</li>
                  <li>Each category shows total spent in the date range</li>
                </ul>

                <h4>Profit Metrics</h4>
                <ul>
                  <li><strong>Total Revenue</strong> - Sum of all completed services</li>
                  <li><strong>Total Expenses</strong> - Sum of all expenses</li>
                  <li><strong>Net Profit</strong> - Revenue minus expenses</li>
                  <li><strong>Profit Margin</strong> - Net profit as percentage of revenue</li>
                </ul>

                <h4>Using Reports</h4>
                <p>Reports help you understand:</p>
                <ul>
                  <li>How much you're making vs spending</li>
                  <li>Which payment methods customers prefer</li>
                  <li>Where your money goes (expense categories)</li>
                  <li>Seasonal trends and profitability</li>
                </ul>
                <p><strong>Awaiting depth:</strong> stops completed before the storm depth was entered have no
                price yet. They are left out of every figure and counted as "Awaiting depth: N stops". The
                Dashboard's Financial Summary shows the same count when it is above 0.</p>
              </AccordionSection>

              <AccordionSection id="settings" title="Settings">
                <h4>How Settings Is Laid Out</h4>
                <p>Settings is one page of seven groups, each opened and closed by its title:</p>
                <ul>
                  <li><strong>Company</strong> - Company Information, Season, Weather Location, Route Optimization</li>
                  <li><strong>Pricing</strong> - Pricing Tiers, Snow depth pricing, Service Add-ons, Public Rates Page, Referral credit (shown in Storm Mode)</li>
                  <li><strong>Customers</strong> - Signup link, Intake Form Disclaimer, Lead Sources, Welcome Message, Service Requests</li>
                  <li><strong>Texting and Email</strong> - How texts are sent, Storm Mode arrival alert, Season Check-in Message</li>
                  <li><strong>Invoicing and Payments</strong> - Square Account, Invoice Defaults, Payment methods and fees, Expense Categories</li>
                  <li><strong>Team</strong> - Team Members</li>
                  <li><strong>Account</strong> - Profile Information, Product Email (owner only), Change Password, Your SnowTrack™ Subscription, Suggest a Feature, Delete Account</li>
                </ul>
                <p>Owners and admins see every group, and Company opens first. Drivers and crew leads see
                Account only. Your Subscription and Delete Account are for the owner only.</p>

                <h4>Profile Settings</h4>
                <p>Update your personal information:</p>
                <ul>
                  <li><strong>First Name / Last Name</strong></li>
                  <li><strong>Email Address</strong> - Used for login and notifications</li>
                  <li><strong>Phone Number</strong></li>
                  <li><strong>Password</strong> - Click "Change Password" to update</li>
                </ul>

                <h4>Company Information</h4>
                <p>Configure your company details:</p>
                <ul>
                  <li><strong>Company Name</strong> - Appears on invoices and public pages</li>
                  <li><strong>Business Phone</strong> - Company phone number</li>
                  <li><strong>Business Email</strong> - Shown to your customers on the portal's Contact Us card</li>
                  <li><strong>Business Address</strong> - Printed at the top of customer statements, and required to email customers: it goes in the footer of every bulk and season check-in email</li>
                  <li><strong>Time zone</strong> - Today's date, texting quiet hours and every time shown follow it</li>
                </ul>

                <h4>Signup link</h4>
                <p>In the Customers group: your public signup link, shown ready to copy, with an optional
                custom ending. The custom ending changes both your signup link and your rates page link,
                and the original links keep working.</p>

                <h4>Public Rates Page</h4>
                <p>In the Pricing group: the link to your public rates page, and the switch{' '}
                <em>Show my prices on my public rates page</em>. When off, the page says pricing is
                available on request and shows only the Sign up button.</p>

                <h4>Pricing Tiers</h4>
                <p>Create customizable pricing categories:</p>
                <ol>
                  <li>Go to <strong>Settings → Pricing → Pricing Tiers</strong></li>
                  <li>Edit existing tiers or click <strong>+ Add Tier</strong></li>
                  <li>For each tier, set:
                    <ul>
                      <li><strong>Tier Name</strong> - e.g., "Small", "Medium", "Large"</li>
                      <li><strong>What this size means</strong> - e.g., "3 to 4 cars"; shown to customers on your signup form</li>
                      <li><strong>Flat price</strong> - Snow removal price, used when depth pricing is off and for routes created before it was switched on</li>
                      <li><strong>Salt Price</strong> - Salt application price</li>
                    </ul>
                  </li>
                  <li>Click <strong>Save Changes</strong></li>
                </ol>
                <p><strong>Note:</strong> Tiers in use by customers are deactivated (not deleted) and can be reactivated.</p>

                <h4>Snow Depth Pricing</h4>
                <p>Price each storm by the measured snow depth instead of one flat price per size:</p>
                <ol>
                  <li>Go to <strong>Settings → Pricing → Snow depth pricing</strong></li>
                  <li>Check the <strong>depth bands</strong>. Each edge includes its own depth, so 3.0 inches is the first band and 3.1 the second. Anything under the first edge bills as the first band.</li>
                  <li>Fill the <strong>grid</strong>: one price per size and band. Empty cells are highlighted. <strong>Fill from flat price</strong> copies each size's flat price into the first band. The <strong>Salt</strong> column is read only and is edited in Pricing Tiers.</li>
                  <li>Set the <strong>On-call premium</strong> (default 20%). It is added to snow, salt and add-ons, before discounts, for customers set to On-call, only on routes priced by depth. An add-on with <strong>On-call premium applies</strong> unticked is left out.</li>
                  <li>Optionally fill in the <strong>Depth source</strong>, e.g. "NWS Green Bay storm total, Appleton"</li>
                  <li>Click <strong>Save depth pricing</strong>, then switch on <strong>Price new snow routes by depth</strong></li>
                </ol>
                <p><strong>Note:</strong> Depth pricing can be switched on only when every price is filled and every active customer has a snow size. It applies to routes created afterwards. Existing routes and completed stops do not change.</p>

                <h4>Entering the Storm Depth</h4>
                <p>On depth-priced routes, completed stops wait for the storm depth before they have a price:</p>
                <ol>
                  <li>Open <strong>Routes</strong> and tap the amber <strong>Awaiting depth</strong> chip, or tap <strong>Enter depth</strong> on the Storm Mode banner</li>
                  <li>Enter the depth in inches and the source, plus any per-city depths</li>
                  <li>Review the preview of old and new prices, the true-up for stops already paid, and any invoiced stops</li>
                  <li>Click <strong>Save depth</strong>. Stops already invoiced need a second tick, then <strong>Bill the difference</strong> on each one</li>
                </ol>
                <p><strong>Tip:</strong> The <strong>Awaiting depth</strong> filter on Routes lists every route still waiting.</p>

                <h4>Invoice Defaults</h4>
                <p>In <strong>Settings → Invoicing and Payments → Invoice Defaults</strong>, set the title and
                message on the Square invoices SnowTrack™ sends for you (a route's own title and message, if set, are used instead):</p>
                <ul>
                  <li><strong>Default Invoice Title</strong> - Leave blank to use the route name</li>
                  <li><strong>Default Invoice Message</strong> - Message body on invoices</li>
                  <li><strong>Send the Square invoice automatically when a stop is completed</strong> - Needs the Pro plan, a connected Square account and the customer's email address</li>
                </ul>
                <p>Available variables:</p>
                <ul>
                  <li>{'{route_name}'} - Route name</li>
                  <li>{'{service_date}'} - Date of service</li>
                  <li>{'{customer_name}'} - Customer name</li>
                </ul>

                <h4>Payment methods and fees</h4>
                <p>In <strong>Settings → Invoicing and Payments → Payment methods and fees</strong>, each
                payment method has one row:</p>
                <ul>
                  <li>A switch to turn it on or off: Square, Venmo, Cash, Cash App, Zelle, Check, and any you add. Only the ones turned on show in Storm Mode and on the customer form. Square shows on the customer form only once your Square account is connected; until then a new customer starts on the first method that is on</li>
                  <li>Its fee, as a percentage plus a flat amount: what you expect to lose to processing. The route print summary uses it to show what a route netted</li>
                  <li>Add your own payment methods; they carry a fee too, and work everywhere a built-in one does. A name cannot be a word SnowTrack already uses (such as Comp), end in &quot;Paid&quot;, or contain &quot;Square&quot;. A custom method can be deleted: stops already paid with it keep its name, its fee stops being counted, and adding the same name again brings it back</li>
                </ul>
                <p>Square's fee here is also what SnowTrack™ records in Expenses when a Square payment
                arrives. With Square turned off, Expenses uses Square's standard invoice rate. One{' '}
                <strong>Save Payment Methods and Fees</strong> button saves the whole card.</p>

                <h4>Square Account</h4>
                <p>Connect the Square account your invoices are sent from:</p>
                <ol>
                  <li>Go to <strong>Settings → Invoicing and Payments → Square Account</strong></li>
                  <li>Click <strong>Connect Square</strong> and sign in to Square</li>
                  <li>Pick a location if your Square account has more than one</li>
                </ol>
                <p>Once connected, the section shows which Square account and location you are
                connected to and when you connected it. <strong>Disconnect</strong> revokes
                SnowTrack™'s access; you can reconnect at any time.</p>

                <h4>Expense Categories</h4>
                <p>New accounts start with none. Click <strong>Add Category</strong> to add your own,
                switch any category on or off, and delete the ones you added. Categories cannot be
                renamed, and built-in ones cannot be deleted, only switched off. See the Expenses
                section above for the built-in list older accounts have.</p>

                <h4>Service Add-ons</h4>
                <p>Configure extra services that can be added to a stop. They appear as options when
                you complete a stop in Storm Mode. Each add-on has a name, a default price, and an
                optional override price per pricing tier. Also for each add-on:</p>
                <ul>
                  <li><strong>Flat $</strong> or <strong>Percent</strong> - A percent is taken of the stop&apos;s snow and salt</li>
                  <li><strong>Show on rates page and signup</strong> - Untick it to keep the add-on off your public pages</li>
                  <li><strong>On-call premium applies</strong> - Untick it to leave the add-on out of the on-call premium</li>
                  <li><strong>Ask for a detail</strong> - A short label, such as &quot;Done by&quot;: when the add-on is ticked on your signup form or on a customer&apos;s add-ons, a box asks for it</li>
                </ul>
                <p><strong>Delete</strong> removes an add-on; one already used on stops is moved to
                Inactive Add-ons instead, and <strong>Reactivate</strong> brings it back. Click{' '}
                <strong>Save Add-ons</strong> to keep your changes.</p>

                <h4>Intake Form: Seasonal or On-call</h4>
                <p>New signups start on <strong>Seasonal Contract</strong>; the customer can choose On-Call
                instead. When you price snow by depth and your on-call premium is above 0, the On-Call choice
                adds the line "On-call service is billed at 20% above seasonal rates" (your percentage).</p>

                <h4>Intake Form Disclaimer</h4>
                <p>Turn on a payment disclaimer for your public intake form and write your own text,
                or click <strong>Use a basic starter</strong>. <code>{'{company_name}'}</code> is filled in with
                your business name. When it is on, a customer has to accept it before they can submit
                the form, and SnowTrack™ keeps a record of the terms each customer agreed to.</p>
                <p>The <strong>Driveway Size Confirmation Line</strong> on the same card is the sentence
                shown under the driveway size question. It stays on even when the disclaimer is off.</p>

                <h4>Welcome Message</h4>
                <p>The message a new signup receives when you confirm their driveway size. Turn it on or off,
                and reword the <strong>Subject</strong> and <strong>Message</strong>; the fields you can use,
                such as <code>{'{{driveway_size}}'}</code>, <code>{'{{snow_price}}'}</code> and{' '}
                <code>{'{{company_phone}}'}</code>, are listed under the message. <strong>Preview</strong> shows
                it, and its text version, for a sample customer. <strong>Reset to default</strong> puts the
                standard wording back until you save.</p>

                <h4>Route Optimization</h4>
                <p>In the Company group: where Storm Mode starts when it puts a route in order. With{' '}
                <strong>Start route optimization from the driver's current location</strong> on, the
                driver's phone location is the starting point. With it off, or if the browser refuses
                the location, it uses the route's starting address, or the last completed stop once
                one is done.</p>

                <h4>Your SnowTrack™ Subscription</h4>
                <p>The Your SnowTrack™ Subscription card, in the Account group (owner only), shows your plan, your subscription status, and your usage of
                users and customers, each against your plan's limit, with an explicit warning if you
                are over one.</p>
                <p><strong>Manage Billing</strong> opens the Stripe billing portal, where you change
                plan, update your card or download invoices. If you have no plan yet, a{' '}
                <strong>View Plans</strong> button takes you to the pricing page instead.</p>
                <p><strong>Cancel Subscription</strong> keeps full access until the end of the period
                you paid for. After that the account is read-only: you can still view customers,
                history and reports and record payments, but cannot run routes, add customers or
                send invoices until you resubscribe. Nothing is deleted. You can set an{' '}
                <strong>Expected return month</strong>, and we email you once about two weeks before.</p>
                <p>For what each plan includes, what happens when you reach a limit, and how held
                intake signups work, see <em>How does pricing work?</em> on the FAQ tab.</p>

                <h4>Lead Sources</h4>
                <p>Configure how customers find you:</p>
                <ul>
                  <li>Facebook, Craigslist, Flyer, Yard Sign, Friend/Referral, Google, Nextdoor, Other</li>
                  <li>Add custom lead sources</li>
                  <li>Lead Source Analytics shows how many customers came from each</li>
                </ul>

                <h4>Referral credit (shown in Storm Mode)</h4>
                <p>In the Pricing group. Shows drivers how much credit a customer has earned for
                referrals in the same storm. It is for reference only: it is not taken off the price or
                off Square invoices. To give the credit, enter it as a discount when you complete the stop.</p>
                <ul>
                  <li><strong>Credit per referral</strong> - Shown for each referred customer serviced in the same storm (routes with the same Storm ID)</li>
                  <li><strong>Lowest price after referral credit</strong> - The price shown never goes below this. It does not set a minimum price for any other stop</li>
                </ul>

                <h4>Weather Location</h4>
                <p>Choose how weather is displayed on your Dashboard:</p>
                <ul>
                  <li><strong>Use the location of the phone or computer you are on</strong> - Weather for where you are</li>
                  <li><strong>Use home location</strong> - Set a fixed city/state for weather</li>
                </ul>
                <p>Home location is useful if you're always checking weather for your service area.</p>
                <p>After you save a home city, if the National Weather Service puts that city in a
                different time zone from your company&apos;s, a line under the card names both zones as the Time zone list does (for example Mountain (Denver)) and offers
                {' '}<strong>Use</strong> that zone; nothing changes unless you click it, and
                {' '}<strong>Dismiss</strong> hides it until your next save.</p>

                <h4>Feature Requests</h4>
                <p>Submit ideas and track their status:</p>
                <ol>
                  <li>Open <strong>Settings → Account → Suggest a Feature</strong></li>
                  <li>Enter subject and description</li>
                  <li>Click <strong>Submit Request</strong></li>
                </ol>
                <p>Track your requests with status updates: New, Under Review, Planned, In Progress, Completed, Declined.
                Our reply appears on the request as "Admin response".</p>
                            </AccordionSection>

              <AccordionSection id="team-management" title="Team Management">
                <h4>User Roles</h4>
                <p>SnowTrack™ supports multiple user roles with different permissions:</p>
                <ul>
                  <li><strong>Owner</strong> - Full access to all features, billing, and team management</li>
                  <li><strong>Admin</strong> - Full access to operations, cannot manage billing or owner settings</li>
                  <li><strong>Crew Lead</strong> - Can view customers (without prices), work assigned routes, use Storm Mode, see My History. Driver pay is set by the owner or an admin</li>
                  <li><strong>Driver</strong> - Limited access to assigned routes, Storm Mode and My History</li>
                </ul>

                <h4>Inviting Team Members</h4>
                <ol>
                  <li>Go to <strong>Settings → Team → Team Members</strong></li>
                  <li>Click <strong>+ Invite Team Member</strong></li>
                  <li>Enter their email address</li>
                  <li>Select their role (Crew Lead or Driver; only the owner can invite an Admin)</li>
                  <li>Click <strong>Send Invitation</strong></li>
                </ol>
                <p>The team member will receive an email with a link, valid for 7 days, to join your team and create their account.</p>

                <h4>Managing Team Members</h4>
                <ul>
                  <li><strong>Edit</strong> - Change a team member's role (only the owner can change an admin)</li>
                  <li><strong>Remove</strong> - Remove a team member from your account; their past work stays on record</li>
                  <li><strong>Cancel</strong> - Withdraw a pending invitation</li>
                </ul>

                <h4>Role Permissions Summary</h4>
                <table style={{width: '100%', borderCollapse: 'collapse', marginTop: '12px'}}>
                  <thead>
                    <tr style={{background: 'var(--color-bg-light, #f3f4f6)'}}>
                      <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb'}}>Feature</th>
                      <th style={{padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb'}}>Owner</th>
                      <th style={{padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb'}}>Admin</th>
                      <th style={{padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb'}}>Crew Lead</th>
                      <th style={{padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb'}}>Driver</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{padding: '6px 8px'}}>Dashboard</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Customers</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>View</td><td style={{textAlign: 'center'}}>-</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Routes</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>Assigned</td><td style={{textAlign: 'center'}}>Assigned</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Storm Mode</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>History</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>Own</td><td style={{textAlign: 'center'}}>Own</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Expenses</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>-</td><td style={{textAlign: 'center'}}>-</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Reports</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>-</td><td style={{textAlign: 'center'}}>-</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Settings</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>Most</td><td style={{textAlign: 'center'}}>Profile</td><td style={{textAlign: 'center'}}>Profile</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Team Management</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>-</td><td style={{textAlign: 'center'}}>-</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Billing</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>-</td><td style={{textAlign: 'center'}}>-</td><td style={{textAlign: 'center'}}>-</td></tr>
                  </tbody>
                </table>

                <h4>Assigning Routes to Drivers</h4>
                <ol>
                  <li>Open a route for editing</li>
                  <li>Select <strong>Assign To</strong> dropdown</li>
                  <li>Choose a team member</li>
                  <li>Save the route</li>
                </ol>
                <p>Crew leads and drivers only see routes assigned to them in their Routes and Storm Mode views.</p>
                <p>Typing the address of a page a role cannot see, such as /reports as a crew lead or /customers as a driver, opens the Dashboard instead.</p>
              </AccordionSection>

              <AccordionSection id="referral-discounts" title="Referral Discounts">
                <h4>How Referrals Work</h4>
                <p>Reward customers who refer new business! When a customer refers someone who also receives
                service, the referring customer gets a discount on their bill.</p>

                <h4>The Importance of Storm ID</h4>
                <p><strong>Key Point:</strong> Referral discounts are tracked <em>per storm</em>. A route must
                have a Storm ID assigned for referral discounts to apply. This ensures discounts are only
                given when both the referrer and referred customer receive service in the same storm event.</p>
                <p>The discount is shown on the stop in Storm Mode for reference. It is not taken off the
                stop's charged price or off Square invoices; use the Discount field when completing the
                stop if you want to charge it.</p>

                <h4>Setting Up Referral Discounts</h4>
                <ol>
                  <li>Go to <strong>Settings → Pricing → Referral credit (shown in Storm Mode)</strong></li>
                  <li>Set the <strong>Credit per referral</strong> (e.g., $5.00)</li>
                  <li>Set the <strong>Lowest price after referral credit</strong></li>
                </ol>

                <h4>Example</h4>
                <p>If your discount is set to $5 per referral:</p>
                <ul>
                  <li>Customer A refers Customer B and Customer C</li>
                  <li>All three customers get service during the same storm</li>
                  <li>Customer A receives a $10 discount (2 referrals × $5)</li>
                </ul>
                <p>The lowest price after referral credit keeps the price shown with the credit from going
                below a set amount (e.g., $10). It applies only to that shown figure, not to any other stop.</p>

                <h4>Setting Up Customer Referrals</h4>
                <p>When adding or editing a customer, use the "Referred By" dropdown to select which
                existing customer referred them. The system works out the discount and shows it on
                the referring customer's stop in Storm Mode.</p>
              </AccordionSection>
            </div>
          )}

          {/* FAQ Tab */}
          {activeTab === 'faq' && (
            <div className="tab-content">
              <h2 className="section-title">Frequently Asked Questions</h2>

              <AccordionSection id="faq-trial" title="How does the free trial work?">
                <p>New accounts start a <strong>free trial</strong> that includes one complete
                storm event: 72 hours from the first stop you complete. A route started inside those 72
                hours can always be finished. You get full access to every feature while the trial runs.
                No credit card is required to start and nothing is charged automatically.</p>
              </AccordionSection>

              <AccordionSection id="faq-pricing" title="How does pricing work?">
                <p>SnowTrack™ uses flat monthly pricing. There are no per-storm charges. Each plan
                includes a set number of users and customers, and unlimited properties under each
                customer. These are the standard prices; current prices are on the{' '}
                <a href="/pricing">Pricing page</a>.</p>
                <ul>
                  <li><strong>Basic, $10/month</strong>: 1 user, 25 customers. Route building and
                  optimization, Storm Mode, weather forecasts, the customer intake form, service
                  history, photo uploads, the customer portal and referral discounts.</li>
                  <li><strong>Pro, $25/month</strong>: 3 users, 75 customers. Everything in Basic,
                  plus Square invoicing from your own account, payment status tracking, expense
                  tracking, profit and loss reports, bulk customer email and customer texting.</li>
                  <li><strong>Premium, $49/month</strong>: 10 users, 200 customers. Everything in Pro,
                  plus priority email support.</li>
                  <li><strong>Enterprise, $99/month</strong>: unlimited users and customers.
                  Everything in Premium, plus a dedicated account manager, custom integrations and
                  onboarding assistance.</li>
                </ul>
                <p>Reaching a limit stops you adding more until you upgrade or free a slot. It never
                removes anything you already have, and routes, Storm Mode, invoicing and reports keep
                working. Deactivating a customer frees a slot. If someone signs up through your public
                intake form while you are full, the signup is held for you rather than turned away,
                and you can take it on from the Customers page once you have room.</p>
                <p>A SnowTrack texting number costs extra on Pro and up: a one-time setup fee ($25 by
                default) and a texting-number line on your subscription ($5 a month by default) once
                carriers approve it. See <em>Texting (Pro)</em> on the Features tab.</p>
              </AccordionSection>

              <AccordionSection id="faq-paid-plan" title="How do I start a paid plan or change plans?">
                <p>Pick Basic, Pro or Premium on the <a href="/pricing">pricing page</a> and pay by card.
                Enterprise is arranged by email instead. Checkout is handled by Stripe, so your card
                details go to Stripe and never to us. The owner changes plan, updates the card or cancels
                in Settings → Account → Your SnowTrack™ Subscription. Plans are month-to-month and there is
                no contract. When you change plan, Stripe prorates the difference.</p>
              </AccordionSection>

              <AccordionSection id="faq-square" title="How do I set up Square invoicing?">
                <p>You connect your own Square account yourself, in Settings. Nobody at
                SnowTrack™ has to do it for you.</p>
                <ol>
                  <li>Go to <strong>Settings → Invoicing and Payments → Square Account</strong></li>
                  <li>Click <strong>Connect Square</strong> and sign in to your Square account</li>
                  <li>If your Square account has more than one location, choose the one invoices
                  should be created under</li>
                </ol>
                <p>Invoices are then sent from your own Square account, and your customers'
                payments land in your own Square balance.</p>
                <p>Once you are connected, make sure your customers have a first name, last name
                and email address, complete the stops on a route, and click <strong>Bill
                All</strong> (Pro tier). Invoices go out for stops where you chose Square, with
                separate line items for snow, salt and each add-on.</p>
                <p><strong>Disconnect</strong>, in the same section, revokes SnowTrack™'s access to
                your Square account and stops invoices being sent from it.</p>
              </AccordionSection>

              <AccordionSection id="faq-multiple-properties" title="Can a customer have multiple properties?">
                <p>Yes! Each customer can have multiple service addresses. Each property can have its own:</p>
                <ul>
                  <li>Service address</li>
                  <li>Pricing</li>
                  <li>Salt preference</li>
                  <li>Notes</li>
                </ul>
              </AccordionSection>

              <AccordionSection id="faq-expenses" title="How do I track expenses?">
                <p>Expense tracking is available on Pro tier and above. Navigate to Expenses and:</p>
                <ol>
                  <li>Click + Add Expense</li>
                  <li>Enter date, amount, category</li>
                  <li>Optionally link to a route</li>
                  <li>View summaries by category</li>
                </ol>
              </AccordionSection>

              <AccordionSection id="faq-reports" title="What reports are available?">
                <p>Pro tier includes Profit & Loss reports showing:</p>
                <ul>
                  <li>Revenue by payment status (Paid/Unpaid)</li>
                  <li>Revenue by payment method</li>
                  <li>Expenses by category</li>
                  <li>Net profit and margin</li>
                </ul>
                <p>Filter by This Month, Last Month, This Season, or custom dates.</p>
              </AccordionSection>

              <AccordionSection id="faq-mobile" title="Does SnowTrack™ work on mobile?">
                <p>Yes! SnowTrack™ is fully responsive and works great on mobile devices:</p>
                <ul>
                  <li>Pull-to-refresh on all pages</li>
                  <li>An update bar at the bottom when a new version is ready; tap it to update</li>
                  <li>Touch-friendly interface</li>
                  <li>Add to home screen for app-like experience (PWA)</li>
                  <li>Tap phone numbers to call or text</li>
                </ul>
              </AccordionSection>

              <AccordionSection id="faq-intake" title="Can customers sign up themselves?">
                <p>Yes. Go to <strong>Settings → Customers → Signup link</strong>,
                where your full link is shown ready to copy. It looks like this:</p>
                <code>https://snowtrack.pro/new-customer/your-company</code>
                <p>The same section lets you set a custom ending for the link, so you can use your
                own business name instead of the code you were given. Customers who open the link
                fill in their details and submit a request for service.</p>
                <p>The form links to your public rates page as <strong>See our rates</strong>, at
                {' '}<code>https://snowtrack.pro/rates/your-company</code>. It shows your prices (or says
                pricing is available on request, if you switch prices off in Settings) and a Sign up
                button back to the form.</p>
              </AccordionSection>
            </div>
          )}

          {/* Contact Tab */}
          {activeTab === 'contact' && (
            <div className="tab-content">
              <h2 className="section-title">Contact & Support</h2>

              <div className="contact-grid">
                <div className="contact-card">
                  <h3>Submit a Feature Request</h3>
                  <p>Have an idea to improve SnowTrack™? We'd love to hear it!</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/settings?section=account')}
                  >
                    Go to Settings → Account → Suggest a Feature
                  </button>
                </div>

                <div className="contact-card">
                  <h3>Email Support</h3>
                  <p>For technical issues or account questions:</p>
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="contact-link">
                    {SUPPORT_EMAIL}
                  </a>
                </div>

                <div className="contact-card">
                  <h3>Track Your Requests</h3>
                  <p>View the status of your submitted feature requests in Settings.</p>
                  <p className="status-legend">
                    <span className="status-badge new">New</span>
                    <span className="status-badge under-review">Under Review</span>
                    <span className="status-badge planned">Planned</span>
                    <span className="status-badge in-progress">In Progress</span>
                    <span className="status-badge completed">Completed</span>
                    <span className="status-badge declined">Declined</span>
                  </p>
                </div>
              </div>

              <div className="tip-box" style={{ marginTop: '24px' }}>
                <strong>Coming Soon:</strong> LawnTrack - The same powerful features for lawn care,
                launching Spring 2027. One account, year-round coverage!
              </div>
            </div>
          )}
        </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="help-footer no-print">
        <p>SnowTrack™ • Last updated {LAST_UPDATED} • © {new Date().getFullYear()}</p>
      </footer>

      <style>{`
        .help-page {
          min-height: 100vh;
          background: var(--color-background, #f5f5f5);
        }

        /* Header - matches Dashboard.js exactly (uses global .app-header from index.css) */
        .app-header-content {
          overflow: hidden;
        }
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 1;
          overflow: hidden;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }
        /* Mobile Menu */
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }
        @media (max-width: 900px) {
          .desktop-nav {
            display: none;
          }
        }

        /* Main Content */
        .help-main {
          max-width: 1000px;
          margin: 0 auto;
          padding: 24px 20px;
        }

        .help-title-section {
          text-align: center;
          margin-bottom: 24px;
        }

        .help-title {
          font-size: 2rem;
          color: #1e88e5;
          margin: 0 0 8px 0;
        }

        .help-subtitle {
          color: #6b7280;
          margin: 0;
        }

        /* Search and Actions */
        .help-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          gap: 16px;
          flex-wrap: wrap;
        }

        .search-box {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-input {
          width: 100%;
          padding: 10px 36px 10px 16px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.9rem;
        }

        .search-input:focus {
          outline: none;
          border-color: #1e88e5;
          box-shadow: 0 0 0 3px rgba(30, 58, 95, 0.1);
        }

        .clear-search {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          font-size: 1.25rem;
          color: #9ca3af;
          cursor: pointer;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        /* Tabs */
        .help-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }

        .help-tab {
          padding: 10px 20px;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 500;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.15s;
        }

        .help-tab:hover {
          background: #f9fafb;
          color: #374151;
        }

        .help-tab.active {
          background: #1e88e5;
          color: white;
          border-color: #1e88e5;
        }

        /* Content */
        .help-content {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }

        .section-title {
          font-size: 1.5rem;
          color: #1e88e5;
          margin: 0 0 16px 0;
        }

        .intro-text {
          color: #4b5563;
          line-height: 1.6;
          margin-bottom: 24px;
        }

        /* Steps Grid */
        .steps-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .step-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
        }

        .step-number {
          width: 32px;
          height: 32px;
          background: #1e88e5;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          margin-bottom: 12px;
        }

        .step-card h3 {
          font-size: 1rem;
          color: #1f2937;
          margin: 0 0 8px 0;
        }

        .step-card p {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0 0 12px 0;
          line-height: 1.5;
        }

        /* Accordion */
        .accordion-section {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          margin-bottom: 12px;
          overflow: hidden;
        }

        .accordion-header {
          width: 100%;
          padding: 16px 20px;
          background: #f9fafb;
          border: none;
          text-align: left;
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: background 0.15s;
        }

        .accordion-header:hover {
          background: var(--color-bg-light, #f3f4f6);
        }

        .accordion-header.expanded {
          background: #1e88e5;
          color: white;
        }

        .accordion-icon {
          font-size: 1.25rem;
          font-weight: 400;
        }

        .accordion-content {
          padding: 20px;
          background: white;
        }

        .accordion-content h4 {
          font-size: 0.95rem;
          color: #1e88e5;
          margin: 16px 0 8px 0;
        }

        .accordion-content h4:first-child {
          margin-top: 0;
        }

        .accordion-content p {
          color: #4b5563;
          line-height: 1.6;
          margin: 0 0 12px 0;
        }

        .accordion-content ul, .accordion-content ol {
          color: #4b5563;
          margin: 0 0 12px 0;
          padding-left: 24px;
        }

        .accordion-content li {
          margin-bottom: 6px;
          line-height: 1.5;
        }

        .accordion-content code {
          background: var(--color-bg-light, #f3f4f6);
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.85rem;
        }

        /* Tip Box */
        .tip-box {
          background: #fef3c7;
          border: 1px solid #fcd34d;
          border-radius: 8px;
          padding: 16px;
          color: #92400e;
        }

        .note-box {
          background: #dbeafe;
          border: 1px solid #93c5fd;
          border-radius: 8px;
          padding: 16px;
          color: #1e40af;
          margin-top: 12px;
        }

        /* Contact Grid */
        .contact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
        }

        .contact-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
        }

        .contact-card h3 {
          font-size: 1rem;
          color: #1f2937;
          margin: 0 0 8px 0;
        }

        .contact-card p {
          color: #6b7280;
          margin: 0 0 12px 0;
        }

        .contact-link {
          color: #1e88e5;
          font-weight: 500;
          text-decoration: none;
        }

        .contact-link:hover {
          text-decoration: underline;
        }

        .status-legend {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 8px;
        }

        .status-badge {
          font-size: 0.7rem;
          padding: 3px 8px;
          border-radius: 10px;
          font-weight: 500;
        }

        .status-badge.new { background: #dbeafe; color: #1e40af; }
        .status-badge.under-review { background: #fef3c7; color: #92400e; }
        .status-badge.planned { background: #ede9fe; color: #6d28d9; }
        .status-badge.in-progress { background: #ffedd5; color: #c2410c; }
        .status-badge.completed { background: #d1fae5; color: #065f46; }
        .status-badge.declined { background: #fee2e2; color: #991b1b; }

        /* Buttons */
        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .btn-sm {
          padding: 6px 12px;
          font-size: 0.8rem;
        }

        .btn-primary {
          background: #1e88e5;
          color: white;
        }

        .btn-primary:hover {
          background: #2d4a73;
        }

        .btn-secondary {
          background: #e5e7eb;
          color: #374151;
        }

        .btn-secondary:hover {
          background: #d1d5db;
        }

        /* Full Manual View */
        .full-manual {
          max-width: 800px;
          margin: 0 auto;
        }

        .full-manual-header {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 32px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e5e7eb;
        }

        .manual-title-section {
          text-align: center;
          margin-bottom: 32px;
        }

        .manual-title-section h1 {
          font-size: 2rem;
          color: #1e88e5;
          margin: 0 0 8px 0;
        }

        .manual-version {
          color: #6b7280;
          font-size: 0.9rem;
        }

        .manual-toc {
          background: #f9fafb;
          padding: 24px;
          border-radius: 8px;
          margin-bottom: 32px;
        }

        .manual-toc h2 {
          margin: 0 0 16px 0;
          font-size: 1.25rem;
          color: #374151;
        }

        .manual-toc ol {
          margin: 0;
          padding-left: 24px;
        }

        .manual-toc li {
          margin-bottom: 8px;
        }

        .manual-toc a {
          color: #1e88e5;
          text-decoration: none;
        }

        .manual-toc a:hover {
          text-decoration: underline;
        }

        .manual-section {
          margin-bottom: 48px;
          padding-bottom: 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .manual-section h2 {
          font-size: 1.5rem;
          color: #1e88e5;
          margin: 0 0 24px 0;
          padding-bottom: 8px;
          border-bottom: 2px solid #1e88e5;
        }

        .manual-section h3 {
          font-size: 1.1rem;
          color: #374151;
          margin: 24px 0 12px 0;
        }

        .manual-section p {
          margin: 0 0 12px 0;
          line-height: 1.6;
          color: #4b5563;
        }

        .manual-section ul, .manual-section ol {
          margin: 0 0 16px 0;
          padding-left: 24px;
        }

        .manual-section li {
          margin-bottom: 6px;
          line-height: 1.5;
          color: #4b5563;
        }

        .manual-section code {
          background: var(--color-bg-light, #f3f4f6);
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.9em;
        }

        .manual-footer {
          text-align: center;
          padding: 32px 0;
          color: #9ca3af;
          font-size: 0.85rem;
        }

        /* Footer */
        .help-footer {
          text-align: center;
          padding: 20px;
          color: #9ca3af;
          font-size: 0.85rem;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .help-actions {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }

          .search-box {
            max-width: none;
          }

          .action-buttons {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .action-buttons .btn {
            width: 100%;
            min-height: 44px;
            font-size: 1rem;
          }

          .help-tabs {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }

          .help-tab {
            min-height: 44px;
            padding: 12px 8px;
            font-size: 0.85rem;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .help-title {
            font-size: 1.5rem;
          }

          .help-subtitle {
            font-size: 0.9rem;
          }

          .steps-grid {
            grid-template-columns: 1fr;
          }

          .full-manual-header {
            flex-direction: column;
            gap: 8px;
          }

          .full-manual-header .btn {
            width: 100%;
            min-height: 44px;
          }
        }

        /* Print Styles */
        @media print {
          .no-print {
            display: none !important;
          }

          .help-page {
            background: white;
          }

          .help-main {
            padding: 0;
            max-width: none;
          }

          .help-content {
            box-shadow: none;
            border: none;
          }

          .accordion-section {
            border: 1px solid #ccc;
            break-inside: avoid;
          }

          .accordion-header {
            background: #f0f0f0 !important;
            color: black !important;
          }

          .accordion-content {
            display: block !important;
          }

          .step-card {
            break-inside: avoid;
          }

          .btn {
            display: none;
          }

          /* Full Manual Print Styles */
          .full-manual {
            max-width: none;
          }

          .manual-title-section h1 {
            color: black;
          }

          .manual-toc {
            background: #f5f5f5;
            page-break-after: always;
          }

          .manual-section {
            page-break-inside: avoid;
          }

          .manual-section h2 {
            color: black;
            border-bottom-color: #333;
          }
        }
      `}</style>
    </div>
  );
}

export default Help;
